import React, { useState, useEffect, useMemo } from "react";
import {
  Clock,
  CheckCircle,
  Send,
  Plus,
  ArrowLeft,
  ClipboardList,
  FileSpreadsheet,
  CircleAlert,
  ClipboardCheck,
  ShieldCheck,
  UploadCloud,
  Camera,
  X as XIcon,
  XCircle,
  ChevronDown,
  MessageSquare,
} from "lucide-react";
import { SafetyHeroBanner } from "../../SafetyBannerArt";

import {
  listPermits,
  listPTWTemplates,
  createPermit,
  resolveActiveProjectId,
  resolveOrgId,
  getPTWTemplate,
  listContractorNamesByOrg,
  getUserGroups,
  permitWorkflowAction,
  submitPermit,
  getPermit,
  downloadPermitReport,
  downloadPermitRegister,
  downloadPermitRegisterExcel,
  downloadPermitTBTAttendanceReport,
  getPermitTbtAttendance,
  savePermitTbtAttendance,
  updatePermitDraft,
  addPermitQuestionLog,
  fetchTowerListByProject,
  fetchTowersByProject,
  reservePermitNumber,
  getProjectDetailsById,
} from "../../../../api";
import { resolveUserOrganizationOrContractor, getUniqueContractorOptions } from "../../../../utils/safetyContractorHelpers";
import { resolveSafetyUserProfile } from "../../../../utils/safetyProfileHelpers";
import { showToast } from "../../../../utils/toast";
import { getCurrentUserName } from "../../../../utils/UserUtils";
import {
  getCurrentGPSLocation,
  fetchSiteGeoFence,
  evaluateLocationStatus,
  formatCoordinate,
  verifySitePresence,
} from "../../../../services/geofenceService";
import PermitSignatureModal from "../utils/PermitSignatureModal";
import PermitHeaderMeta from "../utils/PermitHeaderMeta";
import PermitReadonlyView from "../utils/PermitReadonlyView";
import PTWSignOffSection, { getClosureRemarksList } from "../utils/PTWSignOffSection";
import TbtAttendanceModal from "./TbtAttendanceModal";
import PermitQuestionDiscussion from "../utils/PermitQuestionDiscussion";
import {
  canShowPermitReportDownload,
  formatStatusLabel,
  isEmptyValue,
  normalizeApiErrorMessage,
  validatePermitRequiredFields,
  isProvidedPpeField,
  getPermitWorkflowTimestamps,
  getPermitRejectionBadges,
  extractOptionString,
  extractOptionValue,
  extractAnswerString,
  isClosureRejection,
  isSpecialRowEnabled,
  isSpecialCellDisabled,
  isTemperaturePermissibleLimitCell,
  shouldShowSpecialField,
  evaluateSectionShowWhen,
  cleanHiddenSpecialFields,
  getPermitMakerDisclaimer,
  filterPermitItem,
  buildContractorFilterOptions,
} from "../utils/permitHelpers";
import PermitFilters, { DEFAULT_PERMIT_FILTERS } from "./PermitFilters";
import PermitListMeta, {
  getPermitCardTitle,
  isPermitContractorInternal,
  getPermitInternalTeamName,
  isPermitProject165,
} from "../utils/PermitListMeta";
import { isContractorInternal, resolveContractorName } from "../utils/PermitSignatureCard";
import SafetyImageAnnotationModal from "../../Safety_Inspection/SafetyImageAnnotationModal";
import SafetyCameraCaptureModal from "../../Safety_Inspection/SafetyCameraCaptureModal";
import { buildChecklistSections } from "../utils/permitChecklistSections";
import PermitChecklistSectionTabs from "../utils/PermitChecklistSectionTabs";
import ProvidedPpeBadges from "../utils/ProvidedPpeBadges";
import MultiImageUploadArea from "../utils/MultiImageUploadArea";
import PermitChecklistPhotosList from "../utils/PermitChecklistPhotosList";
import PermitDescriptionBox from "../utils/PermitDescriptionBox";

const getConsentConfigForTemplateAction = (template, action, requiredStatus) => {
  const boxes =
    template?.signature_boxes ||
    template?.checklist_template?.signature_boxes ||
    [];

  const box = boxes.find(
    (b) =>
      (b.action === action || (!b.action && action === "submit")) &&
      (b.required_status === requiredStatus || (!b.required_status && requiredStatus === "draft")) &&
      (b.signer_type === "creator" || b.layout_config?.signer_type === "creator")
  );

  const layout = box?.layout_config || {};

  return {
    enabled: Boolean(layout.consent_enabled),
    text: layout.consent_text || "",
    signature_box_key: box?.key || "",
  };
};

export const isMakerActionablePermit = (item) => {
  if (!item) return false;
  const status = item.workflow_summary?.current_status ?? item.current_status;
  const isFinalRejected =
    item.is_final_rejected ||
    (status === "rejected" &&
      Number(item.issue_rejection_count || 0) >= 3 &&
      !item.workflow_permissions?.can_submit);

  const isFinalCancelled =
    item.is_final_cancelled ||
    (status === "cancelled" &&
      Number(item.cancel_count || 0) >= 3 &&
      !item.workflow_permissions?.can_submit);

  const canResubmitPermit =
    !isFinalRejected &&
    !isFinalCancelled &&
    (item.workflow_permissions?.can_resubmit ||
      (item.workflow_permissions?.can_submit !== false &&
        (status === "rejected" ||
          status === "cancelled" ||
          item.workflow_permissions?.can_submit))) &&
    status !== "closed";
  const canRequestClosure = Boolean(item.workflow_permissions?.can_request_closure);
  return Boolean(canResubmitPermit || canRequestClosure);
};

export const sortMakerPermitsActionableFirst = (list) => {
  if (!Array.isArray(list) || list.length <= 1) return list || [];
  const actionable = [];
  const viewOnly = [];
  list.forEach((item) => {
    if (isMakerActionablePermit(item)) {
      actionable.push(item);
    } else {
      viewOnly.push(item);
    }
  });
  return [...actionable, ...viewOnly];
};

const renderFieldValue = (val) => {
  if (val === null || val === undefined || val === "") return "N/A";
  if (Array.isArray(val)) {
    return val
      .map((item) =>
        typeof item === "object" ? renderFieldValue(item) : String(item),
      )
      .join(", ");
  }
  if (typeof val === "object") {
    const parts = Object.entries(val)
      .filter(([_, v]) => v !== null && v !== undefined && v !== "")
      .map(([k, v]) =>
        typeof v === "object" ? `${k}: ${JSON.stringify(v)}` : String(v),
      );
    return parts.length > 0 ? parts.join(" - ") : "N/A";
  }
  return String(val);
};

const getSpecialSectionCellValue = (permit, section, row, col) => {
  const submittedValue =
    permit?.form_data?.[section.key]?.rows?.[row.key]?.[col.key];

  const rowDefault = row?.[col.key];

  const columnDefault =
    col?.default_value ??
    col?.default ??
    "";

  return submittedValue ?? rowDefault ?? columnDefault ?? "";
};

const renderReadonlySpecialSections = (permit) => {
  const specialSections =
    permit?.resolved_special_sections ||
    permit?.template_snapshot?.special_sections ||
    [];

  if (!specialSections.length) return null;

  return (
    <div className="mb-6 border-b pb-6">
      {/* <h3 className="mb-4 text-lg font-semibold text-slate-800">
        Special Sections
      </h3> */}

      {specialSections.map((section) => (
        <div key={section.key} className="mb-6 last:mb-0">
          <h4 className="mb-3 text-md font-semibold text-slate-800">
            {section.title}
          </h4>

          {section.type === "row_table" ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-slate-300 text-sm">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="w-12 border border-slate-300 p-2 text-left">
                      S.No
                    </th>
                    {(section.columns || []).map((col) => (
                      <th
                        key={col.key}
                        className="border border-slate-300 p-2 text-left"
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {(section.rows || []).map((row, idx) => (
                    <tr key={row.key || idx}>
                      <td className="border border-slate-300 p-2">
                        {idx + 1}
                      </td>

                      {(section.columns || []).map((col) => {
                        if (
                          isSpecialCellDisabled(row, col, section, permit?.form_data)
                        ) {
                          return (
                            <td
                              key={col.key}
                              className="border border-slate-300 p-2 text-slate-800"
                            >
                              {""}
                            </td>
                          );
                        }

                        const value = getSpecialSectionCellValue(
                          permit,
                          section,
                          row,
                          col
                        );

                        return (
                          <td
                            key={col.key}
                            className="border border-slate-300 p-2 text-slate-800"
                          >
                            {renderFieldValue(value)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : section.type === "table" ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {(section.fields || []).map((field) => {
                const value =
                  permit?.form_data?.[section.key]?.[field.key] ??
                  field.default_value ??
                  field.default ??
                  "";

                return (
                  <div
                    key={field.key}
                    className="rounded-lg border border-slate-100 bg-slate-50 p-3"
                  >
                    <p className="text-xs font-medium text-slate-500">
                      {field.label}
                    </p>
                    <p className="mt-1 text-sm font-medium text-slate-900">
                      {renderFieldValue(value)}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : section.type === "conditional_group" ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-semibold text-slate-800">
                {section.left_label || section.title}
                <span className="text-red-500"> *</span>
              </p>

              {section.condition_field && (
                <div className="mt-3">
                  <p className="text-xs font-medium text-slate-500">
                    {section.condition_field.label}
                  </p>
                  <p className="mt-1 text-sm font-medium text-slate-900">
                    {renderFieldValue(
                      permit?.form_data?.[section.key]?.[section.condition_field.key]
                    )}
                  </p>
                </div>
              )}

              {evaluateSectionShowWhen(
                section.show_when,
                permit?.form_data?.[section.key]
              ) &&
                (section.fields || []).map((field) => {
                  if (
                    !shouldShowSpecialField(
                      field,
                      permit?.form_data?.[section.key]
                    )
                  ) {
                    return null;
                  }

                  const value = permit?.form_data?.[section.key]?.[field.key];
                  let displayVal = renderFieldValue(value);

                  if (
                    field.field_type === "select" &&
                    Array.isArray(field.options)
                  ) {
                    const match = field.options.find(
                      (opt) =>
                        String(extractOptionValue(opt)).trim().toLowerCase() ===
                        String(value ?? "").trim().toLowerCase()
                    );
                    if (match) {
                      displayVal = extractOptionString(match);
                    }
                  }

                  return (
                    <div key={field.key} className="mt-3">
                      <p className="text-xs font-medium text-slate-500">
                        {field.label}
                      </p>
                      <p className="mt-1 text-sm font-medium text-slate-900">
                        {displayVal}
                      </p>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-sm text-slate-500">
                Unsupported special section type: {section.type}
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const getQuestionInputType = (question) =>
  String(
    question?.input_type ||
    question?.question_type ||
    question?.field_type ||
    "single_choice"
  )
    .trim()
    .toLowerCase();

const isTextChecklistQuestion = (question) => {
  const type = getQuestionInputType(question);
  return ["text", "textarea", "free_text", "text_input"].includes(type);
};

const isNumberChecklistQuestion = (question) => {
  const type = getQuestionInputType(question);
  return ["number", "numeric"].includes(type);
};

const getAnswerBadgeClasses = (answer) => {
  const normalized = String(answer || "").trim().toLowerCase();

  if (normalized === "yes") {
    return "border-green-500 bg-green-50 text-green-700";
  }
  if (normalized === "no") {
    return "border-red-500 bg-red-50 text-red-700";
  }
  if (normalized === "n/a" || normalized === "na") {
    return "border-yellow-500 bg-yellow-50 text-yellow-700";
  }
  return "border-slate-300 bg-slate-50 text-slate-700";
};

const dataUrlToFile = (input, filename = "signature.png") => {
  if (!input) return input;
  if (typeof File !== "undefined" && input instanceof File) return input;
  if (typeof Blob !== "undefined" && input instanceof Blob) {
    return new File([input], filename, { type: input.type || "image/png" });
  }
  if (typeof input !== "string" || !input.startsWith("data:")) {
    // Not a data URL and not already a File/Blob — return as-is and let the
    // backend's existing URL-fetch fallback in _decode_signature_payload handle it
    // if it turns out to be an http(s) URL instead.
    return input;
  }

  const [header, base64Data] = input.split(",");
  const mimeMatch = header.match(/:(.*?);/);
  const mime = mimeMatch ? mimeMatch[1] : "image/png";
  const binaryStr = atob(base64Data);
  const bytes = new Uint8Array(binaryStr.length);
  for (let i = 0; i < binaryStr.length; i++) {
    bytes[i] = binaryStr.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mime });
};

const formatPermitDateTime = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();

  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const strHours = String(hours).padStart(2, "0");

  return `${day}/${month}/${year} ${strHours}:${minutes} ${ampm}`;
};

const getPermitAssignedGroupName = (item, userGroups = []) => {
  if (!item?.current_assigned_group_id) return "";

  const name = (
    item.current_assigned_group_name ||
    item.workflow_summary?.current_group_name ||
    userGroups.find(
      (g) => Number(g.id) === Number(item.current_assigned_group_id),
    )?.name ||
    `Group ${item.current_assigned_group_id}`
  );
  if (String(name).trim().toLowerCase() === "internal") {
    return getPermitInternalTeamName(item);
  }
  return name;
};

const MAKER_SIDE_ROLES = new Set([
  "MAKER",
  "INTIALIZER",
  "INITIALIZER",
  "INITIALISER",
  "SUPERVISOR",
]);

const getWingOptionLabel = (item) => {
  if (!item) return "";
  if (typeof item === "string") return item.trim();
  return String(
    item.building_name ||
    item.tower_name ||
    item.name ||
    item.wing_name ||
    item.label ||
    ""
  ).trim();
};

export default function PermitMakerDashboard({ activeWorkflowRole }) {
  const normalizedActiveRole = String(
    activeWorkflowRole ||
    localStorage.getItem("FLOW_ROLE") ||
    localStorage.getItem("ROLE") ||
    ""
  )
    .trim()
    .toUpperCase()
    .replace(/[-\s]+/g, "_");

  const canUseMakerPermitFlow = MAKER_SIDE_ROLES.has(normalizedActiveRole);

  const [projectId, setProjectId] = useState(
    () =>
      resolveActiveProjectId?.() ||
      localStorage.getItem("ACTIVE_PROJECT_ID") ||
      "",
  );
  const [wings, setWings] = useState([]);
  const [loadingWings, setLoadingWings] = useState(false);
  const [permits, setPermits] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmittingPermit, setIsSubmittingPermit] = useState(false);
  const [view, setView] = useState("dashboard"); // "dashboard" | "create" | "closure_review" | "view"
  const [collapsedBuckets, setCollapsedBuckets] = useState({});
  const toggleBucket = (title) => {
    setCollapsedBuckets((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const openCreatePermit = () => {
    if (!canUseMakerPermitFlow) {
      showToast("You are not authorized to raise a permit.", "error");
      return;
    }
    setView("create");
  };
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [templateDetails, setTemplateDetails] = useState(null);
  const [formData, setFormData] = useState({});
  const [checklistResponse, setChecklistResponse] = useState({});
  const [checklistImages, setChecklistImages] = useState({});
  const [filters, setFilters] = useState(DEFAULT_PERMIT_FILTERS);
  const [downloadingReportId, setDownloadingReportId] = useState(null);
  const [downloadingTbtReportId, setDownloadingTbtReportId] = useState(null);

  const [reservedPtwNo, setReservedPtwNo] = useState("");
  const [ptwReservationId, setPtwReservationId] = useState(null);
  const [specialSignatureModalOpen, setSpecialSignatureModalOpen] =
    useState(false);
  const [activeSpecialSignature, setActiveSpecialSignature] = useState(null);

  // Custom field states
  const getUserData = () => {
    try {
      const token = localStorage.getItem("ACCESS_TOKEN");
      if (token) {
        return JSON.parse(atob(token.split(".")[1]));
      }
    } catch (e) { }
    try {
      const str = localStorage.getItem("USER_DATA");
      if (str) return JSON.parse(str);
    } catch (e) { }
    return {};
  };
  const userData = getUserData();
  const userProfile = resolveSafetyUserProfile();
  const isInternalUser = React.useMemo(() => {
    const rawType = String(
      userProfile?.user_type || userData?.user_type || ""
    ).trim().toUpperCase();
    if (rawType === "INTERNAL") return true;
    if (rawType === "EXTERNAL") return false;
    const cName = String(
      userProfile?.contractor_name || userData?.contractor_name || ""
    ).trim().toLowerCase();
    return !cName || cName === "internal" || cName === "assigned to internal team";
  }, [userProfile, userData]);

  const [projectContractors, setProjectContractors] = useState([]);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const [contractorSelection, setContractorSelection] = useState("Internal");
  const [customContractorName, setCustomContractorName] = useState("");
  const [contractorError, setContractorError] = useState("");

  const [resolvedOrgContractor, setResolvedOrgContractor] = useState(() =>
    resolveUserOrganizationOrContractor(userProfile, {
      projectId:
        resolveActiveProjectId?.() ||
        localStorage.getItem("ACTIVE_PROJECT_ID"),
    }),
  );

  useEffect(() => {
    let isMounted = true;
    const updateOrgContractor = async () => {
      const activePid =
        projectId ||
        resolveActiveProjectId?.() ||
        localStorage.getItem("ACTIVE_PROJECT_ID");
      let pName = "";
      let projObj = null;
      if (activePid) {
        try {
          const res = await getProjectDetailsById(activePid);
          const data = res?.data;
          projObj = data;
          pName =
            data?.name ||
            data?.project_name ||
            data?.projectName ||
            data?.title ||
            data?.data?.name ||
            data?.data?.project_name ||
            "";
        } catch (e) {
          // ignore error
        }
      }
      if (!isMounted) return;
      const profile = resolveSafetyUserProfile();
      const resolved = resolveUserOrganizationOrContractor(profile, {
        projectId: activePid,
        projectName: pName,
        project: projObj,
      });
      setResolvedOrgContractor(resolved);
      setFormData((prev) => {
        if (!prev) return prev;
        const cur = prev.contractor_name;
        if (!cur || isContractorInternal(cur)) {
          const nextVal = isContractorInternal(resolved) ? "Internal" : resolved;
          return {
            ...prev,
            contractor_name: nextVal,
          };
        }
        return prev;
      });
    };

    updateOrgContractor();

    return () => {
      isMounted = false;
    };
  }, [projectId]);

  useEffect(() => {
    let isMounted = true;
    const fetchContractors = async () => {
      const activePid =
        projectId ||
        resolveActiveProjectId?.() ||
        localStorage.getItem("ACTIVE_PROJECT_ID");
      const activeOid = resolveOrgId?.() || localStorage.getItem("ORG_ID");
      if (!activePid && !activeOid) return;
      try {
        setLoadingContractors(true);
        const res = await listContractorNamesByOrg(activeOid, activePid);
        if (!isMounted) return;
        const raw = res?.data;
        const list = Array.isArray(raw) ? raw : (raw?.results ?? []);
        const unique = getUniqueContractorOptions(list);
        setProjectContractors(unique);
      } catch (err) {
        console.error("Failed to load contractors for permit:", err);
      } finally {
        if (isMounted) setLoadingContractors(false);
      }
    };
    if (isInternalUser) {
      fetchContractors();
    }
    return () => {
      isMounted = false;
    };
  }, [projectId, isInternalUser]);

  const autoContractorName =
    resolvedOrgContractor ||
    userData.contractor_name ||
    userData.company_name ||
    userData.organization_name ||
    userData.org_name ||
    userData.org?.name ||
    userData.company?.name ||
    "Internal";
  const autoApplicantName =
    getCurrentUserName() ||
    ([userData.first_name, userData.last_name].filter(Boolean).join(" ")) ||
    userData.name ||
    userData.full_name ||
    userData.username ||
    "";
  const [locationParts, setLocationParts] = useState({
    wing: "",
    floor: "",
    flat: "",
  });

  const [userGroups, setUserGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");

  // Closure modal state
  const [closureModalOpen, setClosureModalOpen] = useState(false);
  const [selectedPermitForClosure, setSelectedPermitForClosure] =
    useState(null);
  const [closureRemarks, setClosureRemarks] = useState("");
  const [closureGroupId, setClosureGroupId] = useState("");

  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [tbtModalOpen, setTbtModalOpen] = useState(false);
  const [tbtSheet, setTbtSheet] = useState(null);
  const [tbtRows, setTbtRows] = useState([
    {
      person_name: "",
      contractor_name: "",
      designation: "",
      signature: "",
    },
  ]);

  const [annotationOpen, setAnnotationOpen] = useState(false);
  const [annotationFile, setAnnotationFile] = useState(null);
  const [annotationTargetQuestionId, setAnnotationTargetQuestionId] = useState(null);
  const [annotationQueue, setAnnotationQueue] = useState([]);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraTargetQuestionId, setCameraTargetQuestionId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const pid =
        resolveActiveProjectId?.() || localStorage.getItem("ACTIVE_PROJECT_ID");
      setProjectId(pid);

      const [permitsRes, templatesRes] = await Promise.all([
        listPermits({ project_id: pid, created_by_me: true, refresh_workflow: true }),
        listPTWTemplates(),
      ]);

      setPermits(
        Array.isArray(permitsRes?.data)
          ? permitsRes.data
          : permitsRes?.data?.results || [],
      );
      setTemplates(
        Array.isArray(templatesRes?.data)
          ? templatesRes.data
          : templatesRes?.data?.results || [],
      );
    } catch (err) {
      showToast("Failed to load permits", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await getUserGroups();
        const data = Array.isArray(res?.data)
          ? res.data
          : res?.data?.results || [];
        setUserGroups(data);
      } catch (err) {
        console.error("Failed to load user groups", err);
      }
    };
    fetchGroups();
  }, []);

  // Synchronize projectId if view opens create mode or localStorage changes
  useEffect(() => {
    if (view === "create") {
      const pid =
        resolveActiveProjectId?.() ||
        localStorage.getItem("ACTIVE_PROJECT_ID") ||
        "";
      if (pid && String(pid) !== String(projectId)) {
        setProjectId(pid);
      }
    }
  }, [view, projectId]);

  useEffect(() => {
    const handleStorageChange = () => {
      const pid =
        resolveActiveProjectId?.() ||
        localStorage.getItem("ACTIVE_PROJECT_ID") ||
        "";
      if (pid && String(pid) !== String(projectId)) {
        setProjectId(pid);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [projectId]);

  // Fetch project wings dynamically
  useEffect(() => {
    const activePid =
      projectId ||
      resolveActiveProjectId?.() ||
      localStorage.getItem("ACTIVE_PROJECT_ID");

    if (!activePid) {
      setWings([]);
      return;
    }

    let isMounted = true;
    const fetchWings = async () => {
      setLoadingWings(true);
      try {
        let list = [];
        try {
          const res = await fetchTowerListByProject(activePid);
          const data = res?.data ?? res;
          const raw = Array.isArray(data)
            ? data
            : data?.buildings || data?.results || data?.data || [];
          if (Array.isArray(raw) && raw.length > 0) {
            list = raw;
          }
        } catch (err) {
          console.warn(
            "fetchTowerListByProject failed, trying fetchTowersByProject:",
            err,
          );
        }

        if (list.length === 0) {
          try {
            const fallbackRes = await fetchTowersByProject(activePid);
            const fallbackData = fallbackRes?.data ?? fallbackRes;
            const rawFallback = Array.isArray(fallbackData)
              ? fallbackData
              : fallbackData?.buildings ||
                fallbackData?.results ||
                fallbackData?.data ||
                [];
            if (Array.isArray(rawFallback) && rawFallback.length > 0) {
              list = rawFallback;
            }
          } catch (err2) {
            console.warn("fetchTowersByProject fallback failed:", err2);
          }
        }

        if (isMounted) {
          setWings(list);
        }
      } catch (error) {
        console.error("Failed to load wings for project:", error);
        if (isMounted) {
          setWings([]);
        }
      } finally {
        if (isMounted) {
          setLoadingWings(false);
        }
      }
    };

    fetchWings();

    return () => {
      isMounted = false;
    };
  }, [projectId]);

  useEffect(() => {
    if (view === "resubmit" && selectedPermit) {
      let cr = selectedPermit?.checklist_response || [];
      if (typeof cr === "string") {
        try {
          cr = JSON.parse(cr);
        } catch (_) {
          cr = [];
        }
      }
      if (Array.isArray(cr) && cr.length > 0 && Object.keys(checklistResponse).length === 0) {
        const answerMap = {};
        const commentsMap = {};
        cr.forEach((row) => {
          const qid = row?.question_id ?? row?.id;
          if (qid !== undefined && qid !== null) {
            answerMap[String(qid)] = {
              question_id: qid,
              answer: extractAnswerString(row.answer),
              remarks: row.remarks || row.comment || "",
            };
            if (row.remarks || row.comment) {
              commentsMap[qid] = row.remarks || row.comment;
            }
          }
        });
        setChecklistResponse(answerMap);
        setResubmitQuestionComments((prev) => ({ ...commentsMap, ...prev }));
      }
    }
  }, [view, selectedPermit]);

  useEffect(() => {
    if (view === "create" && selectedTemplate) {
      const fetchTemplate = async () => {
        try {
          const res = await getPTWTemplate(selectedTemplate);
          setTemplateDetails(res?.data);
          const pid =
            resolveActiveProjectId?.() ||
            localStorage.getItem("ACTIVE_PROJECT_ID");

          const orgId =
            resolveOrgId?.() || localStorage.getItem("ORG_ID") || "";

          const reservationRes = await reservePermitNumber({
            template_id: selectedTemplate,
            project_id: pid || projectId || "",
            organization_id: orgId,
          });

          setReservedPtwNo(reservationRes?.data?.ptw_no || "");
          setPtwReservationId(reservationRes?.data?.reservation_id || null);

          const initialData = {};
          res?.data?.fields_config?.forEach((fc) => {
            const fd = fc.field_definition;
            if (fd) {
              const k = fd.key;
              const k_lower = k.toLowerCase();
              const l_lower = (fd.label || "").toLowerCase();
              if (
                k_lower === "contractor_name" ||
                k_lower.includes("contractor")
              ) {
                initialData[k] = resolvedOrgContractor || autoContractorName;
              } else if (
                k_lower === "permit_applicant" ||
                k_lower === "applicant_name" ||
                k_lower.includes("applicant") ||
                l_lower.includes("applicant")
              ) {
                initialData[k] = autoApplicantName;
              }
            }
          });

          setContractorSelection("Internal");
          setCustomContractorName("");
          setContractorError("");
          setFormData({
            ...initialData,
            contractor_name: resolvedOrgContractor || autoContractorName,
            permit_applicant: autoApplicantName,
          });
          setChecklistResponse({});
          setChecklistImages({});
          setLocationParts({ wing: "", floor: "", flat: "" });
          setSelectedGroupId("");
        } catch (err) {
          showToast("Failed to load template details", "error");
          setTemplateDetails(null);
        }
      };
      fetchTemplate();
    } else {
      const initialData = {};
      templateDetails?.fields_config?.forEach((fc) => {
        const fd = fc.field_definition;
        if (fd) {
          const k = fd.key;
          const k_lower = k.toLowerCase();
          const l_lower = (fd.label || "").toLowerCase();
          if (k_lower === "contractor_name" || k_lower.includes("contractor")) {
            initialData[k] = resolvedOrgContractor || autoContractorName;
          } else if (
            k_lower === "permit_applicant" ||
            k_lower === "applicant_name" ||
            k_lower.includes("applicant") ||
            l_lower.includes("applicant")
          ) {
            initialData[k] = autoApplicantName;
          }
        }
      });
      setTemplateDetails(null);
      setFormData({
        ...initialData,
        contractor_name: resolvedOrgContractor || autoContractorName,
        permit_applicant: autoApplicantName,
      });
      setChecklistResponse({});
      setChecklistImages({});
      setLocationParts({ wing: "", floor: "", flat: "" });
      setSelectedGroupId("");
    }
  }, [selectedTemplate, view]);

  // const getFirstChecklistQuestion = () => {
  //   return templateDetails?.checklist_template?.questions?.[0] || null;
  // };

  // const openTbtModal = () => {
  //   const firstQuestion = getFirstChecklistQuestion();

  //   if (!firstQuestion) {
  //     showToast("First checklist question not found", "error");
  //     return;
  //   }

  //   setTbtModalOpen(true);
  // };

  // const updateTbtRow = (index, key, value) => {
  //   setTbtRows((prev) =>
  //     prev.map((row, i) =>
  //       i === index
  //         ? {
  //           ...row,
  //           [key]: value,
  //         }
  //         : row
  //     )
  //   );
  // };

  // const addTbtRow = () => {
  //   setTbtRows((prev) => [
  //     ...prev,
  //     {
  //       person_name: "",
  //       contractor_name: "",
  //       designation: "",
  //       signature: "",
  //     },
  //   ]);
  // };

  // const removeTbtRow = (index) => {
  //   setTbtRows((prev) => {
  //     if (prev.length === 1) return prev;
  //     return prev.filter((_, i) => i !== index);
  //   });
  // };

  // TBT Modal state variables managed by TbtAttendanceModal now

  const getCleanedTbtRows = () => {
    const firstQuestionId =
      (templateDetails?.checklist_template?.questions?.[0] || templateDetails?.questions?.[0])?.id || null;

    return tbtRows
      .map((row) => ({
        question_id: firstQuestionId,
        person_name: String(row.person_name || "").trim(),
        contractor_name: String(row.contractor_name || "").trim(),
        designation: String(row.designation || "").trim(),
        signature: row.signature || "",
      }))
      .filter(
        (row) =>
          row.person_name ||
          row.contractor_name ||
          row.designation ||
          row.signature,
      );
  };

  const openSpecialSignatureModal = ({ sectionKey, signatureKey }) => {
    setActiveSpecialSignature({
      sectionKey,
      signatureKey,
    });
    setSpecialSignatureModalOpen(true);
  };

  const handleSpecialSignatureSuccess = (signatureDataUrl) => {
    if (!activeSpecialSignature) return;

    const { sectionKey, signatureKey } = activeSpecialSignature;

    setFormData((prev) => ({
      ...prev,
      [sectionKey]: {
        ...(prev[sectionKey] || {}),
        [signatureKey]: signatureDataUrl,
      },
    }));

    setSpecialSignatureModalOpen(false);
    setActiveSpecialSignature(null);
  };

  // Sync location
  useEffect(() => {
    if (locationParts.wing || locationParts.floor || locationParts.flat) {
      setFormData((prev) => ({ ...prev, location: { ...locationParts } }));
    } else {
      setFormData((prev) => ({ ...prev, location: {} }));
    }
  }, [locationParts]);

  const handleRequestClosure = async (signatureData = null, sigMeta = null) => {
    if (!signatureData) {
      setPendingAction(() => (sig, meta) => handleRequestClosure(sig, meta));
      setSignatureModalOpen(true);
      return;
    }

    if (isSubmittingPermit) return;
    setIsSubmittingPermit(true);

    try {
      const payload = {
        action: "request_closure",
        remarks: closureRemarks,
        signature: signatureData,
      };
      if (sigMeta?.consent_accepted) {
        payload.consent_accepted = true;
      }
      if (closureGroupId) {
        payload.next_group_id = closureGroupId;
      }
      await permitWorkflowAction(selectedPermitForClosure.id, payload);
      showToast("Permit closure requested successfully", "success");
      setView("dashboard");
      setSelectedPermit(null);
      setSelectedPermitForClosure(null);
      setClosureRemarks("");
      setClosureGroupId("");
      fetchData();
    } catch (err) {
      const data = err?.response?.data;
      const detail =
        data?.consent ||
        data?.signature ||
        data?.detail ||
        data?.message ||
        err?.message;

      const detailStr = typeof detail === "string" ? detail : JSON.stringify(detail || "");

      if (
        detailStr.toLowerCase().includes("signature") ||
        detailStr.toLowerCase().includes("consent")
      ) {
        setPendingAction(() => (sig, meta) => handleRequestClosure(sig, meta));
        setSignatureModalOpen(true);
      } else {
        const message = normalizeApiErrorMessage(err);
        showToast(message || "Failed to request closure", "error");
      }
    } finally {
      setIsSubmittingPermit(false);
    }
  };

  const [resubmitQuestionComments, setResubmitQuestionComments] = useState({});
  const [resubmitOverallRemarks, setResubmitOverallRemarks] = useState("");

  const getLatestRejectLog = (permit) => {
    const logs = Array.isArray(permit?.workflow_logs) ? permit.workflow_logs : [];
    return [...logs]
      .filter((log) => String(log?.action).toLowerCase() === "reject")
      .sort(
        (a, b) =>
          new Date(b?.performed_at || b?.created_at || 0) -
          new Date(a?.performed_at || a?.created_at || 0)
      )[0];
  };

  const getRejectedQuestionIds = (permit) => {
    if (Array.isArray(permit?.rejected_question_ids)) {
      return permit.rejected_question_ids.map(Number);
    }

    const latestRejectLog = getLatestRejectLog(permit);
    if (!latestRejectLog?.id) return [];

    const logs = permit?.question_logs || {};
    const ids = [];

    Object.entries(logs).forEach(([questionId, bucket]) => {
      const comments = Array.isArray(bucket?.comments) ? bucket.comments : [];
      const hasRejectComment = comments.some((comment) => {
        const sameWorkflow =
          Number(comment?.workflow_log) === Number(latestRejectLog.id);
        const source = String(comment?.source || "").toLowerCase();
        const isReviewSource =
          source.includes("reject") ||
          source === "issue_reject" ||
          source === "closure_reject";
        return sameWorkflow || isReviewSource;
      });
      if (hasRejectComment) {
        ids.push(Number(questionId));
      }
    });

    return [...new Set(ids)];
  };

  const getCheckerRejectionRemarkForQuestion = (permit, questionId) => {
    if (!permit || !questionId) return null;

    const qidStr = String(questionId);
    const qidNum = Number(questionId);
    const qLogs = permit?.question_logs?.[qidStr] || permit?.question_logs?.[qidNum] || {};
    const comments = Array.isArray(qLogs?.comments) ? qLogs.comments : [];

    const rejectComment = [...comments]
      .reverse()
      .find((c) => {
        const src = String(c?.source || "").toLowerCase();
        return (
          src === "issue_reject" ||
          src === "closure_reject" ||
          src.includes("reject")
        );
      });

    const latestRejectLog = getLatestRejectLog(permit);
    const fallbackTimestamp =
      latestRejectLog?.performed_at ||
      latestRejectLog?.created_at ||
      permit?.rejected_at ||
      permit?.updated_at ||
      "";

    if (rejectComment && rejectComment.comment) {
      return {
        comment: rejectComment.comment,
        user_name: rejectComment.user_name || rejectComment.created_by_name || "",
        created_at:
          rejectComment.created_at ||
          rejectComment.timestamp ||
          rejectComment.performed_at ||
          fallbackTimestamp,
      };
    }

    const wfComments = Array.isArray(permit?.comments) ? permit.comments : [];
    const matched = wfComments.find(
      (c) =>
        Number(c?.question_id) === qidNum &&
        (Number(c?.workflow_log) === Number(latestRejectLog?.id) ||
          String(c?.source || "").toLowerCase().includes("reject"))
    );

    if (matched && matched.comment) {
      return {
        comment: matched.comment,
        user_name: matched.user_name || latestRejectLog?.performed_by_name || "",
        created_at:
          matched.created_at ||
          matched.timestamp ||
          latestRejectLog?.performed_at ||
          latestRejectLog?.created_at ||
          fallbackTimestamp,
      };
    }

    return null;
  };

  const handleOpenPermitForResubmission = async (permitItem) => {
    try {
      const res = await getPermit(permitItem.id);
      const detail = res?.data?.permit || res?.permit || res?.data || res;
      setSelectedPermit(detail);

      const answerMap = {};
      const initialComments = {};
      let cr = detail?.checklist_response || [];
      if (typeof cr === "string") {
        try {
          cr = JSON.parse(cr);
        } catch (_) {
          cr = [];
        }
      }
      (cr || []).forEach((row) => {
        const qid = row?.question_id ?? row?.id;
        if (qid !== undefined && qid !== null) {
          answerMap[String(qid)] = {
            question_id: qid,
            answer: extractAnswerString(row.answer),
            remarks: row.remarks || row.comment || "",
          };
          if (row.remarks || row.comment) {
            initialComments[qid] = row.remarks || row.comment;
          }
        }
      });
      setChecklistResponse(answerMap);
      setResubmitQuestionComments(initialComments);

      if (detail.template_id) {
        setSelectedTemplate(detail.template_id);
        try {
          const tRes = await getPTWTemplate(detail.template_id);
          const tData = tRes?.data?.template || tRes?.template || tRes?.datFa || tRes;
          setTemplateDetails(tData);
        } catch (_) { /* no-op */ }
      }

      setResubmitOverallRemarks("");
      setView("resubmit");
    } catch (err) {
      showToast("Failed to load permit details for resubmission", "error");
    }
  };

  const handleResubmitRejectedPermit = async (signatureData = null) => {
    if (!selectedPermit) return;

    const isCancelledStatus = selectedPermit?.current_status === "cancelled";
    const isClosureStatus = isClosureRejection(selectedPermit);
    const targetSource = isCancelledStatus
      ? "maker_cancel_resubmit"
      : isClosureStatus
      ? "maker_closure_resubmit"
      : "maker_resubmit";
    const targetIds = isCancelledStatus
      ? (selectedPermit?.cancelled_question_ids || []).map(Number)
      : getRejectedQuestionIds(selectedPermit);
    const targetSet = new Set(targetIds.map(Number));
    const hasQuestionFeedback = targetIds.length > 0;

    if (isClosureStatus) {
      if (!resubmitOverallRemarks.trim()) {
        showToast("Please provide an overall resubmission remark.", "error");
        return;
      }
    } else if (!isCancelledStatus && targetIds.length === 0) {
      // In rejection mode, targetIds cannot be empty. In cancellation mode, targetIds can be empty (Scenario A).
      showToast(
        "No specific checklist point was marked for correction. Please contact checker/admin.",
        "error"
      );
      return;
    }

    if (!isClosureStatus) {
      const existingRows = selectedPermit?.checklist_response || [];
      const updatedRows = existingRows.map((row) => {
        const qid = row?.question_id ?? row?.id;
        const isTarget = (!isCancelledStatus || hasQuestionFeedback)
          ? targetSet.has(Number(qid))
          : true; // In Scenario A (overall cancellation only), ALL rows can be updated

        if (!isTarget) {
          return row;
        }
        const updated = checklistResponse[String(qid)] || row;
        const makerRemark =
          resubmitQuestionComments?.[qid] !== undefined &&
          resubmitQuestionComments[qid] !== null &&
          String(resubmitQuestionComments[qid]).trim() !== ""
            ? String(resubmitQuestionComments[qid]).trim()
            : (updated.remarks || row.remarks || "");

        const resolvedAns = extractAnswerString(updated.answer) || extractAnswerString(row.answer) || "";
        return {
          ...row,
          answer: resolvedAns,
          remarks: makerRemark,
        };
      });

      const missing = updatedRows.filter((row) => {
        const ans = extractAnswerString(row.answer);
        if (!isCancelledStatus || hasQuestionFeedback) {
          return targetSet.has(Number(row.question_id)) && !ans;
        }
        return !ans;
      });

      if (missing.length > 0) {
        showToast(
          isCancelledStatus
            ? "Please answer all required checklist points before resubmitting."
            : "Please answer all rejected checklist points.",
          "error"
        );
        return;
      }

      const missingComments = targetIds.filter(
        (qid) => !String(resubmitQuestionComments?.[qid] || "").trim()
      );
      if (missingComments.length > 0) {
        showToast(
          "Please provide a resubmission remark for all rejected checklist points.",
          "error"
        );
        return;
      }
    }

    if (!signatureData) {
      setPendingAction(() => (sig) => handleResubmitRejectedPermit(sig));
      setSignatureModalOpen(true);
      return;
    }

    if (isSubmittingPermit) return;
    setIsSubmittingPermit(true);

    try {
      if (!isClosureStatus) {
        const existingRows = selectedPermit?.checklist_response || [];
        const updatedRows = existingRows.map((row) => {
          const qid = row?.question_id ?? row?.id;
          const isTarget = (!isCancelledStatus || hasQuestionFeedback)
            ? targetSet.has(Number(qid))
            : true;
          if (!isTarget) return row;
          const updated = checklistResponse[String(qid)] || row;
          const makerRemark =
            resubmitQuestionComments?.[qid] !== undefined &&
            resubmitQuestionComments[qid] !== null &&
            String(resubmitQuestionComments[qid]).trim() !== ""
              ? String(resubmitQuestionComments[qid]).trim()
              : (updated.remarks || row.remarks || "");
          const resolvedAns = extractAnswerString(updated.answer) || extractAnswerString(row.answer) || "";
          return {
            ...row,
            answer: resolvedAns,
            remarks: makerRemark,
          };
        });

        // Step 1: Update permit draft with corrected checklist response
        await updatePermitDraft(selectedPermit.id, {
          checklist_response: updatedRows,
        });
      }

      // Step 2: Formatted comments for submit endpoint
      const formattedComments = Object.entries(resubmitQuestionComments || {})
        .filter(([qid, comment]) => {
          if (!isCancelledStatus || hasQuestionFeedback) {
            return targetSet.has(Number(qid)) && String(comment || "").trim();
          }
          return String(comment || "").trim();
        })
        .map(([qid, comment]) => ({
          question_id: Number(qid),
          comment: String(comment).trim(),
          source: targetSource,
          comment_type: "PUBLIC",
        }));

      // Dynamic GPS capture and geofence evaluation
      let userCoords = null;
      try {
        const targetPid = selectedPermit?.project_id || projectId;
        const geoCheck = await verifySitePresence({ projectId: targetPid });
        if (!geoCheck.allowed) {
          showToast(geoCheck.message || "Location verification failed.", "error");
          return;
        }
        if (!geoCheck.isBypassed && geoCheck.coords) {
          userCoords = geoCheck.coords;
        }
      } catch (geoErr) {
        const userMsg =
          geoErr.userMessage ||
          geoErr.message ||
          "Location access is required to resubmit a Permit to Work. Please enable GPS permissions and try again.";
        showToast(userMsg, "error");
        return;
      }

      // Step 3: Submit permit with fresh maker signature and optional remarks or question_comments
      const submitPayload = {
        signature: signatureData,
      };
      if (userCoords) {
        submitPayload.user_latitude = formatCoordinate(userCoords.latitude, 7);
        submitPayload.user_longitude = formatCoordinate(userCoords.longitude, 7);
        if (userCoords.accuracy !== undefined && userCoords.accuracy !== null) {
          submitPayload.user_location_accuracy = userCoords.accuracy;
        }
      }
      if (isClosureStatus) {
        submitPayload.remarks = resubmitOverallRemarks.trim();
      } else if (formattedComments.length > 0) {
        submitPayload.question_comments = JSON.stringify(formattedComments);
      }

      await submitPermit(selectedPermit.id, submitPayload);

      showToast("Permit resubmitted successfully", "success");
      setView("dashboard");
      setResubmitOverallRemarks("");
      setResubmitQuestionComments({});
      setChecklistResponse({});
      fetchData();
    } catch (err) {
      const message = normalizeApiErrorMessage(err);
      showToast(message || "Failed to resubmit permit", "error");
    } finally {
      setIsSubmittingPermit(false);
    }
  };

  const handleChecklistImagesChange = (questionId, files) => {
    const selectedFiles = Array.from(files || []);

    if (!selectedFiles.length) return;

    const imageFiles = selectedFiles.filter((file) =>
      String(file?.type || "").startsWith("image/"),
    );

    if (!imageFiles.length) {
      showToast("Please upload valid image files only.", "error");
      return;
    }

    if (imageFiles.length !== selectedFiles.length) {
      showToast("Some non-image files were skipped.", "warning");
    }

    setAnnotationTargetQuestionId(questionId);
    setAnnotationQueue(imageFiles);
    setAnnotationFile(imageFiles[0]);
    setAnnotationOpen(true);
  };

  const handleOpenChecklistCamera = (questionId) => {
    setCameraTargetQuestionId(questionId);
    setCameraOpen(true);
  };

  const handleChecklistCameraCapture = (capturedFile) => {
    const questionId = cameraTargetQuestionId;

    if (
      questionId === null ||
      questionId === undefined ||
      !capturedFile
    ) {
      return;
    }

    if (!String(capturedFile?.type || "").startsWith("image/")) {
      showToast("Unable to capture a valid image.", "error");
      return;
    }

    setAnnotationTargetQuestionId(questionId);
    setAnnotationQueue([capturedFile]);
    setAnnotationFile(capturedFile);
    setAnnotationOpen(true);
  };

  const handleAnnotatedChecklistImageSave = (annotatedFile) => {
    if (
      annotatedFile &&
      annotationTargetQuestionId !== null &&
      annotationTargetQuestionId !== undefined
    ) {
      setChecklistImages((prev) => ({
        ...prev,
        [annotationTargetQuestionId]: [
          ...(prev[annotationTargetQuestionId] || []),
          annotatedFile,
        ],
      }));
    }

    setAnnotationQueue((prev) => {
      const nextQueue = prev.slice(1);

      if (nextQueue.length) {
        setAnnotationFile(nextQueue[0]);
        setAnnotationOpen(true);
      } else {
        setAnnotationOpen(false);
        setAnnotationFile(null);
        setAnnotationTargetQuestionId(null);
      }

      return nextQueue;
    });
  };

  const handleAnnotationClose = () => {
    setAnnotationOpen(false);
    setAnnotationFile(null);
    setAnnotationTargetQuestionId(null);
    setAnnotationQueue([]);
  };

  const removeChecklistImage = (questionId, index) => {
    setChecklistImages((prev) => {
      const current = prev[questionId] || [];
      const next = current.filter((_, i) => i !== index);

      return {
        ...prev,
        [questionId]: next,
      };
    });
  };

  const getFirstChecklistQuestion = () => {
    return (
      templateDetails?.checklist_template?.questions?.[0] ||
      templateDetails?.questions?.[0] ||
      selectedPermit?.template_snapshot?.checklist_questions?.[0] ||
      null
    );
  };

  const openTbtModal = () => {
    const firstQuestion = getFirstChecklistQuestion();

    if (!firstQuestion && !selectedPermit) {
      showToast("First checklist question not found", "error");
      return;
    }

    setTbtModalOpen(true);
  };

  const updateTbtRow = (index, key, value) => {
    setTbtRows((prev) =>
      prev.map((row, i) =>
        i === index
          ? {
            ...row,
            [key]: value,
          }
          : row,
      ),
    );
  };

  const addTbtRow = () => {
    setTbtRows((prev) => [
      ...prev,
      {
        person_name: "",
        contractor_name: "",
        designation: "",
        signature: "",
      },
    ]);
  };

  const saveTbtAttendance = () => {
    // Logic moved to TbtAttendanceModal, local save sets state directly via setter
  };

  const extractNumbers = (text) => {
    const matches = String(text || "").match(/\d+(\.\d+)?/g);
    return matches ? matches.map(Number) : [];
  };

  const validateReadingAgainstLimit = (reading, permissibleLimit) => {
    if (reading === "" || reading === null || reading === undefined) {
      return true;
    }

    const readingNumber = Number(String(reading).replace(/[^\d.]/g, ""));
    if (Number.isNaN(readingNumber)) return true;

    const nums = extractNumbers(permissibleLimit);

    if (!nums.length) return true;

    // Example: Oxygen 19.5 – 21%
    if (nums.length >= 2) {
      return readingNumber >= nums[0] && readingNumber <= nums[1];
    }

    // Example: 50ppm Max, 1200ppm Max, 10ppm Max, 1% LEL
    return readingNumber <= nums[0];
  };

  const validateSpecialSectionsBeforeSubmit = () => {
    const sections = templateDetails?.special_sections || [];

    for (const section of sections) {
      if (section.type === "row_table") {
        for (const row of section.rows || []) {
          // 1. Skip entire row if row is disabled
          if (!isSpecialRowEnabled(row, section, formData)) {
            continue;
          }

          for (const col of section.columns || []) {
            // 2. Skip individual cell if cell is disabled
            if (isSpecialCellDisabled(row, col, section, formData)) {
              continue;
            }

            const isColRequired = Boolean(
              col.required || col.is_required || col.mandatory
            );
            const cellValue =
              formData?.[section.key]?.rows?.[row.key]?.[col.key];
            const isBlank =
              cellValue === undefined ||
              cellValue === null ||
              String(cellValue).trim() === "";

            const editableBy = col.editable_by || ["maker"];
            const makerCanEdit =
              col.editable &&
              (Array.isArray(editableBy)
                ? editableBy.includes("maker")
                : editableBy === "maker");

            // Validate required field on enabled row & enabled cell
            if (makerCanEdit && isColRequired && isBlank) {
              const rowLabel = row.gas || row.label || row.name || row.key;
              const colLabel = col.label || col.key;
              return {
                valid: false,
                message: `${rowLabel}: ${colLabel} is required.`,
              };
            }

            // Permissible limit comparison validation
            if (
              col.validation?.type === "compare_with_permissible_limit" &&
              !row.skip_reading_validation &&
              !isBlank
            ) {
              const limitKey = col.validation.permissible_field;
              const permissibleLimit =
                formData?.[section.key]?.rows?.[row.key]?.[limitKey] ??
                row?.[limitKey] ??
                "";

              const isValid = validateReadingAgainstLimit(
                cellValue,
                permissibleLimit,
              );

              if (!isValid) {
                const rowLabel = row.gas || row.label || row.name || row.key;
                return {
                  valid: false,
                  message: `${rowLabel}: Reading should be within permissible limit (${permissibleLimit}).`,
                };
              }
            }
          }
        }
      } else if (section.type === "conditional_group") {
        const conditionField = section.condition_field;

        if (conditionField) {
          const conditionValue = formData?.[section.key]?.[conditionField.key];

          // Isolation List Yes/No selector is always mandatory, regardless of template config
          if (isEmptyValue(conditionValue)) {
            return {
              valid: false,
              message: `Please select an option for "${conditionField.label || section.left_label || section.title}".`,
            };
          }

          const conditionMet =
            !section.show_when ||
            evaluateSectionShowWhen(section.show_when, formData?.[section.key]);

          // Only enforce the sub-fields when the condition (e.g. "Yes") is actually selected
          if (conditionMet) {
            for (const field of section.fields || []) {
              const shouldHide = !shouldShowSpecialField(
                field,
                formData?.[section.key],
              );

              if (shouldHide) continue;

              const fieldValue = formData?.[section.key]?.[field.key];



              const isShiftChangeIsolationField =
                String(field.label || "").toLowerCase().includes("shift change") &&
                String(field.label || "").toLowerCase().includes("isolation");

              const isFieldRequired = field.required || isShiftChangeIsolationField;
              if (isFieldRequired && isEmptyValue(fieldValue)) {
                return {
                  valid: false,
                  message: `Please fill required field: ${field.label} (${section.conditional_title || section.title || section.left_label
                    }).`,
                };
              }
            }
          }
        }
      }
    }

    return { valid: true };
  };


  const handleCreateDraft = async (signatureData = null, sigMeta = null) => {
    if (isSubmittingPermit) return;
    if (!selectedTemplate) {
      return showToast("Select a template", "error");
    }

    setIsSubmittingPermit(true);
    try {
      const missingFields = (templateDetails.fields_config || []).filter(
        (fc) => {
          const fd = fc.field_definition;
          const required =
            fc.required_override ?? fd?.required_default ?? fc?.required ?? fd?.required ?? false;
          const key = fd?.key || fc?.key;
          const value = formData?.[key];

          return required && isEmptyValue(value);
        },
      );

      if (missingFields.length) {
        const firstField = missingFields[0];
        const fieldKey = firstField.field_definition?.key || firstField.key;
        if (fieldKey === "location") {
          return showToast("Location is required. Please enter permit location.", "error");
        }
        const label = firstField.field_definition?.label || firstField.label || "field";
        return showToast(
          `Please fill required field: ${label}`,
          "error",
        );
      }

      const missingChecklist = (
        templateDetails.checklist_template?.questions ||
        templateDetails.questions ||
        []
      ).filter((q) => {
        if (!q.is_required) return false;
        const answer = checklistResponse[q.id]?.answer;
        return answer === null || answer === undefined || String(answer).trim() === "";
      });

      if (missingChecklist.length) {
        return showToast(
          `Please answer checklist item: ${missingChecklist[0].question}`,
          "error",
        );
      }

      if (isInternalUser && contractorSelection === "Other") {
        if (!customContractorName || !customContractorName.trim()) {
          setContractorError("Please enter a contractor name.");
          setIsSubmittingPermit(false);
          return showToast("Please enter a contractor name.", "error");
        }
      }

      const specialValidation = validateSpecialSectionsBeforeSubmit();

      if (!specialValidation.valid) {
        return showToast(specialValidation.message, "error");
      }

      // Dynamic GPS capture and geofence evaluation
      let userCoords = null;
      try {
        const geoCheck = await verifySitePresence({ projectId });
        if (!geoCheck.allowed) {
          showToast(geoCheck.message || "Location verification failed.", "error");
          return;
        }
        if (!geoCheck.isBypassed && geoCheck.coords) {
          userCoords = geoCheck.coords;
        }
      } catch (geoErr) {
        const userMsg =
          geoErr.userMessage ||
          geoErr.message ||
          "Location access is required to submit a Permit to Work. Please enable GPS permissions and try again.";
        showToast(userMsg, "error");
        return;
      }

      const formattedChecklist = Object.values(checklistResponse);
      const fd = new FormData();

      fd.append("template_id", String(selectedTemplate));
      fd.append("project_id", String(projectId || ""));
      fd.append(
        "organization_id",
        String(resolveOrgId?.() || localStorage.getItem("ORG_ID") || ""),
      );
      if (userCoords) {
        fd.append("user_latitude", String(formatCoordinate(userCoords.latitude, 7)));
        fd.append("user_longitude", String(formatCoordinate(userCoords.longitude, 7)));
        if (userCoords.accuracy !== undefined && userCoords.accuracy !== null) {
          fd.append("user_location_accuracy", String(userCoords.accuracy));
        }
      }
      fd.append("submit_now", "true");
      if (selectedGroupId) {
        fd.append("assigned_group_id", String(selectedGroupId));
      }
      const cleanedFormData = { ...(formData || {}) };
      if (isInternalUser) {
        if (contractorSelection === "Other") {
          const trimmedName = (customContractorName || "").trim();
          cleanedFormData.contractor_name = trimmedName;
          cleanedFormData.contractor_selection = "Other";
          cleanedFormData._contractor_selection = "Other";
        } else if (contractorSelection && !isContractorInternal(contractorSelection)) {
          cleanedFormData.contractor_name = contractorSelection;
          cleanedFormData.contractor_selection = contractorSelection;
        } else {
          cleanedFormData.contractor_name = "Internal";
          cleanedFormData.contractor_selection = "Internal";
        }
      } else {
        if (!cleanedFormData.contractor_name) {
          if (resolvedOrgContractor && !isContractorInternal(resolvedOrgContractor)) {
            cleanedFormData.contractor_name = resolvedOrgContractor;
          } else {
            cleanedFormData.contractor_name = "Internal";
          }
        } else if (isContractorInternal(cleanedFormData.contractor_name)) {
          cleanedFormData.contractor_name = "Internal";
        }
      }
      if (Array.isArray(templateDetails?.special_sections)) {
        templateDetails.special_sections.forEach((sec) => {
          if (sec.type === "row_table") {
            if (!cleanedFormData[sec.key]) {
              cleanedFormData[sec.key] = {};
            }
            if (!cleanedFormData[sec.key].rows) {
              cleanedFormData[sec.key].rows = {};
            }

            const disabledRows = [];

            sec.rows?.forEach((r) => {
              const rowEnabled = isSpecialRowEnabled(r, sec, formData);
              if (!rowEnabled) {
                disabledRows.push(r.key);
              }

              if (!cleanedFormData[sec.key].rows[r.key]) {
                cleanedFormData[sec.key].rows[r.key] = {};
              }

              // Preserve enabled / disabled state for backend validation
              cleanedFormData[sec.key].rows[r.key].enabled = rowEnabled;
              cleanedFormData[sec.key].rows[r.key].disabled = !rowEnabled;

              sec.columns?.forEach((c) => {
                const cellDisabled = isSpecialCellDisabled(r, c, sec, formData);
                if (cellDisabled) {
                  const val = cleanedFormData[sec.key].rows[r.key][c.key];
                  if (val === undefined || val === null || String(val).trim() === "") {
                    delete cleanedFormData[sec.key].rows[r.key][c.key];
                  }
                }
              });
            });

            if (disabledRows.length > 0) {
              cleanedFormData[sec.key].disabled_rows = disabledRows;
            }
          }
        });
      }

      fd.append("form_data", JSON.stringify(cleanedFormData));
      fd.append("checklist_response", JSON.stringify(formattedChecklist || []));
      fd.append("ptw_no", reservedPtwNo || "");
      if (ptwReservationId) {
        fd.append("ptw_number_reservation_id", String(ptwReservationId));
      }

      if (signatureData) {
        const testImage = dataUrlToFile(
          signatureData,
          `maker_signature_${Date.now()}.png`,
        );
        console.log("found signature data:", signatureData);
        console.log(testImage);
        fd.append("maker_signature", testImage);
        if (sigMeta?.consent_accepted) {
          fd.append("consent_accepted", "true");
        }
      }

      Object.entries(checklistImages || {}).forEach(([questionId, files]) => {
        (files || []).forEach((file) => {
          fd.append(`checklist_images_${questionId}`, file);
        });
      });

      const createRes = await createPermit(fd);

      const createdPermit =
        createRes?.data?.data || createRes?.data || createRes;

      const createdPermitId = createdPermit?.id;

      if (createdPermitId) {
        const cleanedTbtRows = getCleanedTbtRows();

        if (cleanedTbtRows.length > 0 || tbtSheet) {
          const tbtFormData = new FormData();

          // Add sheet data
          const sheetData = { ...(tbtSheet || {}) };
          if (sheetData.issued_date && sheetData.issued_date.includes("-")) {
            const parts = sheetData.issued_date.split("-");
            sheetData.issued_date = `${parts[2]}/${parts[1]}/${parts[0]}`;
          }
          const todayD = new Date();
          const dd = String(todayD.getDate()).padStart(2, "0");
          const mm = String(todayD.getMonth() + 1).padStart(2, "0");
          const yyyy = todayD.getFullYear();
          sheetData.date_of_training = `${dd}/${mm}/${yyyy}`;

          if (
            sheetData.training_conducted_by_sign &&
            sheetData.training_conducted_by_sign.startsWith("data:")
          ) {
            const sigFile = dataUrlToFile(
              sheetData.training_conducted_by_sign,
              `trainer_sign_${Date.now()}.png`,
            );
            tbtFormData.append("training_conducted_by_sign", sigFile);
            delete sheetData.training_conducted_by_sign;
          } else {
            delete sheetData.training_conducted_by_sign;
          }
          tbtFormData.append("sheet", JSON.stringify(sheetData));

          const rowsWithoutBase64 = cleanedTbtRows.map((row, index) => {
            if (row.signature) {
              const signatureFile = dataUrlToFile(
                row.signature,
                `tbt_signature_${index + 1}_${Date.now()}.png`,
              );

              tbtFormData.append(`signature_${index}`, signatureFile);
            }

            return {
              question_id: row.question_id,
              person_name: row.person_name,
              contractor_name: row.contractor_name,
              designation: row.designation,
            };
          });

          tbtFormData.append("rows", JSON.stringify(rowsWithoutBase64));

          await savePermitTbtAttendance(createdPermitId, tbtFormData);
        }
      }

      showToast("Permit raised successfully", "success");
      setView("dashboard");
      setChecklistImages({});
      setChecklistResponse({});
      setTbtRows([
        {
          person_name: "",
          contractor_name: "",
          designation: "",
          signature: "",
        },
      ]);
      setTbtSheet(null);
      fetchData();
    } catch (err) {
      const data = err?.response?.data;
      const detail =
        data?.consent ||
        data?.signature ||
        data?.detail ||
        data?.message ||
        err?.message;

      const detailStr = typeof detail === "string" ? detail.toLowerCase() : "";

      if (
        detailStr.includes("signature") ||
        detailStr.includes("consent")
      ) {
        setPendingAction(() => (sig, sigMeta) => handleCreateDraft(sig, sigMeta));
        setSignatureModalOpen(true);
      } else {
        const message = normalizeApiErrorMessage(err);
        showToast(message, "error");
      }
    } finally {
      setIsSubmittingPermit(false);
    }
  };

  const handleOpenClosureReview = async (permit) => {
    try {
      const res = await getPermit(permit.id);
      setSelectedPermit(res.data);
      setSelectedPermitForClosure(res.data);
      setClosureRemarks("");
      setClosureGroupId("");
      setView("closure_review");
    } catch (err) {
      showToast("Failed to load permit details", "error");
    }
  };

  const handleViewPermit = async (permit) => {
    try {
      const res = await getPermit(permit.id);
      setSelectedPermit(res.data);
      setView("view");
    } catch (err) {
      showToast("Failed to load permit details", "error");
    }
  };

  // Download handler
  const handleDownloadReport = async (permit) => {
    if (!permit?.id) return;
    setDownloadingReportId(permit.id);
    try {
      await downloadPermitReport(permit.id);
      showToast("Report downloaded successfully", "success");
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to download permit report";
      showToast(message, "error");
    } finally {
      setDownloadingReportId(null);
    }
  };

  const handleDownloadTbtReport = async (permit) => {
    if (!permit?.id) return;
    setDownloadingTbtReportId(permit.id);
    try {
      await downloadPermitTBTAttendanceReport(permit.id);
      showToast("TBT Attendance Report downloaded successfully", "success");
    } catch (err) {
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to download TBT attendance report";
      showToast(message, "error");
    } finally {
      setDownloadingTbtReportId(null);
    }
  };

  const [downloadingRegisterExcel, setDownloadingRegisterExcel] = useState(false);

  const handleDownloadRegister = async () => {
    try {
      const pid =
        resolveActiveProjectId?.() ||
        localStorage.getItem("ACTIVE_PROJECT_ID") ||
        projectId;
      await downloadPermitRegister(pid);
      showToast("Register downloaded successfully", "success");
    } catch (err) {
      let msg = err?.message || "Failed to download register";
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          msg = json?.detail || json?.message || msg;
        } catch {}
      } else if (err?.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      showToast(msg, "error");
    }
  };

  const handleDownloadRegisterExcel = async () => {
    setDownloadingRegisterExcel(true);
    try {
      const pid =
        resolveActiveProjectId?.() ||
        localStorage.getItem("ACTIVE_PROJECT_ID") ||
        projectId;
      await downloadPermitRegisterExcel(pid);
      showToast("Excel register downloaded successfully", "success");
    } catch (err) {
      let msg = err?.message || "Failed to download Excel register";
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          msg = json?.detail || json?.message || msg;
        } catch {}
      } else if (err?.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      showToast(msg, "error");
    } finally {
      setDownloadingRegisterExcel(false);
    }
  };

  const getStatus = (p) =>
    p.workflow_summary?.current_status ?? p.current_status;

  const contractorOptions = useMemo(
    () => buildContractorFilterOptions(permits, [], projectId),
    [permits, projectId]
  );

  const filteredPermits = useMemo(() => {
    return permits.filter((p) => filterPermitItem(p, filters));
  }, [permits, filters]);

  const pendingApproval = useMemo(
    () =>
      sortMakerPermitsActionableFirst(
        filteredPermits.filter((p) => getStatus(p) === "issue_in_progress")
      ),
    [filteredPermits]
  );
  const pendingClosure = useMemo(
    () =>
      sortMakerPermitsActionableFirst(
        filteredPermits.filter((p) => getStatus(p) === "closure_in_progress")
      ),
    [filteredPermits]
  );
  const issuedPermits = useMemo(
    () =>
      sortMakerPermitsActionableFirst(
        filteredPermits.filter((p) => getStatus(p) === "issued")
      ),
    [filteredPermits]
  );
  const closedPermits = useMemo(
    () =>
      sortMakerPermitsActionableFirst(
        filteredPermits.filter((p) => getStatus(p) === "closed" && !p.is_final_rejected)
      ),
    [filteredPermits]
  );
  const rejectedPermits = useMemo(
    () =>
      sortMakerPermitsActionableFirst(
        filteredPermits.filter((p) => getStatus(p) === "rejected" || p.is_final_rejected)
      ),
    [filteredPermits]
  );
  const cancelledPermits = useMemo(
    () =>
      sortMakerPermitsActionableFirst(
        filteredPermits.filter((p) => getStatus(p) === "cancelled")
      ),
    [filteredPermits]
  );

  const renderPermitCard = (item, badgeClass) => {
    const isFinalRejected =
      item.is_final_rejected ||
      (getStatus(item) === "rejected" &&
        Number(item.issue_rejection_count || 0) >= 3 &&
        !item.workflow_permissions?.can_submit);

    const isFinalCancelled =
      item.is_final_cancelled ||
      (getStatus(item) === "cancelled" &&
        Number(item.cancel_count || 0) >= 3 &&
        !item.workflow_permissions?.can_submit);

    return (
      <div
        key={item.id}
        className="flex flex-col gap-2.5 border-b border-border/50 p-4 last:border-0"
      >
        {/* ROW 1: Title (left) & Status + Primary Action Button (top right corner) */}
        <div className="flex items-start justify-between gap-2.5 sm:gap-3">
          <h3 className="min-w-0 flex-1 font-semibold text-foreground text-sm sm:text-base leading-snug break-words">
            {getPermitCardTitle(item)}
          </h3>

          {(() => {
            const canResubmitPermit =
              !isFinalRejected &&
              !isFinalCancelled &&
              (item.workflow_permissions?.can_resubmit ||
                (item.workflow_permissions?.can_submit !== false &&
                  (getStatus(item) === "rejected" ||
                    getStatus(item) === "cancelled" ||
                    item.workflow_permissions?.can_submit))) &&
              getStatus(item) !== "closed";
            const canRequestClosure = Boolean(item.workflow_permissions?.can_request_closure);
            const hasWorkflowAction = canResubmitPermit || canRequestClosure;

            return (
              <div className="flex shrink-0 flex-col items-end gap-1.5 pt-0.5">
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5 sm:gap-2">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap ${isFinalRejected || isFinalCancelled
                      ? "bg-red-100 text-red-800 border border-red-200"
                      : badgeClass
                      }`}
                  >
                    {isFinalRejected
                      ? "Final Rejected"
                      : isFinalCancelled
                        ? "Final Cancelled"
                        : formatStatusLabel(getStatus(item))}
                  </span>

                  {hasWorkflowAction ? (
                    <>
                      {canResubmitPermit && (
                        <button
                          type="button"
                          onClick={() => handleOpenPermitForResubmission(item)}
                          className="inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-orange-400 to-orange-500 px-3 py-1 text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md whitespace-nowrap shrink-0 cursor-pointer"
                        >
                          Resubmit
                        </button>
                      )}
                      {canRequestClosure && (
                        <button
                          type="button"
                          onClick={() => handleOpenClosureReview(item)}
                          className="inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-orange-400 to-orange-500 px-3 py-1 text-xs font-semibold text-white shadow-sm transition-all hover:shadow-md whitespace-nowrap shrink-0 cursor-pointer"
                        >
                          Request Closure
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleViewPermit(item)}
                      className="inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100 whitespace-nowrap shrink-0 cursor-pointer"
                    >
                      View
                    </button>
                  )}
                </div>

                {hasWorkflowAction && (
                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => handleViewPermit(item)}
                      className="inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 transition-colors hover:bg-blue-100 whitespace-nowrap shrink-0 cursor-pointer"
                    >
                      View
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* ROW 3: Full-width Metadata Badges */}
        <PermitListMeta item={item} projectId={projectId} />

        {/* ROW 2: Timestamps & Waiting on */}
        <div className="text-xs text-muted-foreground">
          {(() => {
            const ts = getPermitWorkflowTimestamps(item);
            return (
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 gap-y-0.5 font-normal">
                {ts.startTimeText && (
                  <span>{ts.startTimeText}</span>
                )}
                {ts.endTimeText && (
                  <span>
                    {ts.startTimeText ? "· " : ""}{ts.endTimeText}
                  </span>
                )}
                {item.current_assigned_group_id && (
                  <span className="text-slate-600">
                    {ts.startTimeText || ts.endTimeText ? "· " : ""}Waiting on: {getPermitAssignedGroupName(item, userGroups)}
                  </span>
                )}
                {ts.mode === "closed" && ts.closedTimeDisplay && (
                  <span className="text-slate-500">
                    {ts.startTimeText || ts.endTimeText || item.current_assigned_group_id ? "· " : ""}Closed: {ts.closedTimeDisplay}
                  </span>
                )}
              </div>
            );
          })()}
        </div>
        {getStatus(item) === "cancelled" && (
          <div className="mt-1 flex flex-col gap-0.5">
            {(item.latest_cancelled_by_name || item.latest_cancel_reason) && (
              <p className="text-xs text-red-600 font-medium">
                Cancelled by {item.latest_cancelled_by_name || "Checker"}
                {item.latest_cancel_reason ? `: "${item.latest_cancel_reason}"` : ""}
                {item.latest_cancelled_at_display ? ` on ${item.latest_cancelled_at_display}` : ""}
              </p>
            )}
            <p className="text-xs font-semibold text-red-600">
              Cancellation {item.cancel_count || 1} of 3
              {isFinalCancelled ? " · Limit Reached (Final Cancelled)" : ""}
            </p>
            {isFinalCancelled && (
              <p className="text-xs text-red-700 italic">
                This permit has reached the cancellation limit and cannot be resubmitted.
              </p>
            )}
          </div>
        )}
        {getStatus(item) === "rejected" && (
          <div className="mt-1 flex flex-col gap-0.5">
            {item.closure_rejection_count > 0 ? (
              <p className="text-xs font-semibold text-amber-600">
                Closure Rejection {item.closure_rejection_count} of 3
              </p>
            ) : (
              <p className="text-xs font-semibold text-orange-600">
                Approval Rejection {item.issue_rejection_count || 1} of 3
                {isFinalRejected ? " · Limit Reached (Final Rejected)" : ""}
              </p>
            )}
            {isFinalRejected && (
              <p className="text-xs text-red-700 italic">
                This permit has reached the issue rejection limit and cannot be resubmitted.
              </p>
            )}
          </div>
        )}
        {getStatus(item) !== "rejected" && getStatus(item) !== "cancelled" && (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {item.issue_rejection_count > 0 && (
              <span className="text-xs font-medium text-orange-600">
                Approval Rejection {item.issue_rejection_count} of 3
              </span>
            )}
            {item.closure_rejection_count > 0 && (
              <span className="text-xs font-medium text-amber-600">
                Closure Rejection {item.closure_rejection_count} of 3
              </span>
            )}
            {item.cancel_count > 0 && (
              <span className="text-xs font-medium text-red-600">
                Cancellation {item.cancel_count} of 3
              </span>
            )}
          </div>
        )}

        {/* ROW 2: Report / Attendance Download Actions (if applicable) */}
        {(() => {
          const canDownload = canShowPermitReportDownload(item);
          if (!canDownload) return null;

          return (
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 pt-0.5">
              <button
                type="button"
                onClick={() => handleDownloadReport(item)}
                disabled={downloadingReportId === item.id}
                className="rounded-lg bg-green-600 px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-all hover:bg-green-700 disabled:opacity-50 whitespace-nowrap shrink-0 cursor-pointer"
              >
                {downloadingReportId === item.id
                  ? "Downloading..."
                  : "Download Report"}
              </button>
              <button
                type="button"
                onClick={() => handleDownloadTbtReport(item)}
                disabled={downloadingTbtReportId === item.id}
                className="rounded-lg bg-indigo-600 px-2.5 py-1 text-xs font-semibold text-white shadow-sm transition-all hover:bg-indigo-700 disabled:opacity-50 whitespace-nowrap shrink-0 cursor-pointer"
              >
                {downloadingTbtReportId === item.id
                  ? "Downloading..."
                  : "Download TBT Attendance"}
              </button>
            </div>
          );
        })()}
      </div>
    );
  };

  const renderPermitBucket = (title, items, icon, colorClass, badgeClass) => {
    const isCollapsed = !!collapsedBuckets[title];
    const actionableItems = [];
    const viewOnlyItems = [];
    (items || []).forEach((item) => {
      if (isMakerActionablePermit(item)) {
        actionableItems.push(item);
      } else {
        viewOnlyItems.push(item);
      }
    });

    const hasActionable = actionableItems.length > 0;
    const hasViewOnly = viewOnlyItems.length > 0;

    return (
      <div className="flex flex-col">
        <div
          className="mb-2 flex cursor-pointer items-center gap-2 select-none"
          onClick={() => toggleBucket(title)}
        >
          {icon}
          <h2
            className="text-sm font-semibold text-foreground sm:text-base"
            style={{ color: colorClass }}
          >
            {title}
          </h2>
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {(items || []).length}
          </span>
          <ChevronDown
            className={`ml-auto h-4 w-4 text-muted-foreground transition-transform ${isCollapsed ? "-rotate-90" : ""
              }`}
          />
        </div>

        {!isCollapsed && (
          <div className="h-[480px] overflow-y-auto rounded-xl border bg-card shadow-sm">
            {!items || items.length === 0 ? (
              <div className="flex h-full items-center justify-center">
                <p className="text-sm text-muted-foreground">No items</p>
              </div>
            ) : (
              <>
                {hasActionable && (
                  <div data-testid="group-action-required">
                    <div className="sticky top-0 z-10 flex items-center justify-between border-b border-amber-200/80 bg-amber-50/95 px-4 py-1.5 backdrop-blur-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block h-2 w-2 rounded-full bg-amber-500 animate-pulse" />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-amber-900">
                          Action Required
                        </span>
                      </div>
                      <span className="rounded-full bg-amber-200/80 px-2 py-0.5 text-[10px] font-extrabold text-amber-900">
                        {actionableItems.length}
                      </span>
                    </div>
                    {actionableItems.map((item) => renderPermitCard(item, badgeClass))}
                  </div>
                )}

                {hasViewOnly && (
                  <div data-testid="group-view-only">
                    <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-slate-100/95 px-4 py-1.5 backdrop-blur-xs">
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block h-2 w-2 rounded-full bg-slate-400" />
                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-700">
                          View Only
                        </span>
                      </div>
                      <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-extrabold text-slate-700">
                        {viewOnlyItems.length}
                      </span>
                    </div>
                    {viewOnlyItems.map((item) => renderPermitCard(item, badgeClass))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50/60 p-4 sm:p-6 lg:p-8">
      {view === "dashboard" && (
        <div className="mx-auto w-full max-w-[1600px] space-y-6">
          <SafetyHeroBanner
            type="permit"
            categoryTag="PERMIT TO WORK SYSTEM"
            categoryIcon={ShieldCheck}
            title="Permit To Work"
            subtitle="Safety permit management dashboard — authorize, monitor, and sign off site permits."
          >
            <button
              type="button"
              onClick={handleDownloadRegisterExcel}
              disabled={downloadingRegisterExcel}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-white/90 backdrop-blur-sm px-4 py-2.5 text-xs sm:text-sm font-semibold text-emerald-700 transition-all hover:bg-emerald-50 hover:shadow-sm disabled:opacity-50 cursor-pointer shadow-xs"
            >
              <FileSpreadsheet className="h-4 w-4" />
              {downloadingRegisterExcel ? "Downloading..." : "Download Register"}
            </button>
            {canUseMakerPermitFlow && (
              <button
                onClick={openCreatePermit}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-2.5 text-xs sm:text-sm font-semibold text-white shadow-sm transition-all hover:shadow-md hover:from-orange-600 hover:to-orange-700 cursor-pointer"
              >
                <Plus className="h-4 w-4" />
                Raise Permit
              </button>
            )}
          </SafetyHeroBanner>

          <PermitFilters
            filters={filters}
            setFilters={setFilters}
            templates={templates}
            contractors={contractorOptions}
            showDraftOption={true}
          />

          {loading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              Loading...
            </div>
          ) : (
            <>
              {/* Counter cards */}
              <div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
                {[
                  {
                    key: "issue_in_progress",
                    label: "Pending for Approval",
                    icon: <Clock className="h-3.5 w-3.5" />,
                    colorCls: "hsl(45, 93%, 47%)",
                    count: pendingApproval.length,
                  },
                  {
                    key: "closure_in_progress",
                    label: "Pending for Closer",
                    icon: <Send className="h-3.5 w-3.5" />,
                    colorCls: "hsl(24.6, 95%, 53.1%)",
                    count: pendingClosure.length,
                  },
                  {
                    key: "issued",
                    label: "Issued",
                    icon: <CheckCircle className="h-3.5 w-3.5" />,
                    colorCls: "hsl(145, 65%, 42%)",
                    count: issuedPermits.length,
                  },
                  {
                    key: "closed",
                    label: "Closed",
                    icon: <CheckCircle className="h-3.5 w-3.5" />,
                    colorCls: "hsl(200, 70%, 45%)",
                    count: closedPermits.length,
                  },
                  {
                    key: "rejected",
                    label: "Rejected",
                    icon: <CircleAlert className="h-3.5 w-3.5" />,
                    colorCls: "hsl(0, 75%, 55%)",
                    count: rejectedPermits.length,
                  },
                  {
                    key: "cancelled",
                    label: "Cancelled",
                    icon: <XCircle className="h-3.5 w-3.5" />,
                    colorCls: "hsl(220, 9%, 46%)",
                    count: cancelledPermits.length,
                  },
                  {
                    key: "all",
                    label: "Total",
                    icon: <ClipboardList className="h-3.5 w-3.5" />,
                    colorCls: "hsl(263, 83%, 53%)",
                    count: filteredPermits.length,
                  },
                ].map(({ key, label, icon, colorCls, count }) => {
                  const isSelected = filters.status === key;
                  return (
                    <div
                      key={label}
                      onClick={() => {
                        setFilters((prev) => ({
                          ...prev,
                          status: prev.status === key ? "all" : key,
                        }));
                      }}
                      className={`group relative flex flex-col justify-between h-full rounded-2xl border bg-card p-3.5 sm:p-4 shadow-2xs transition-all duration-200 cursor-pointer select-none hover:-translate-y-0.5 hover:shadow-md ${
                        isSelected
                          ? "ring-2 ring-offset-1 border-transparent shadow-sm"
                          : "border-slate-200/90 hover:border-slate-300"
                      }`}
                      style={{
                        borderColor: isSelected ? colorCls : undefined,
                        boxShadow: isSelected ? `0 0 0 2px ${colorCls}40` : undefined,
                      }}
                      title={`Filter by ${label}`}
                    >
                      {/* Top Row: Icon Badge + Label with fixed min-height for uniform alignment */}
                      <div className="flex items-start gap-2 min-h-[38px]">
                        <span
                          className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg transition-colors"
                          style={{
                            backgroundColor: `${colorCls}18`,
                            color: colorCls,
                          }}
                        >
                          {icon}
                        </span>
                        <span
                          className="text-xs sm:text-[13px] font-semibold leading-snug line-clamp-2"
                          style={{ color: colorCls }}
                        >
                          {label}
                        </span>
                      </div>

                      {/* Bottom Row: Count strictly aligned to horizontal baseline */}
                      <div className="mt-3 flex items-baseline border-t border-border/40 pt-2.5">
                        <span className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
                          {Number.isFinite(Number(count)) ? Number(count) : 0}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Bucket Lists */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {renderPermitBucket(
                  "Pending for Approval",
                  pendingApproval,
                  <Clock className="h-4 w-4 text-muted-foreground" />,
                  "hsl(45, 93%, 47%)",
                  "bg-yellow-100 text-yellow-800",
                )}

                {renderPermitBucket(
                  "Pending for Closer",
                  pendingClosure,
                  <Send className="h-4 w-4 text-muted-foreground" />,
                  "hsl(24.6, 95%, 53.1%)",
                  "bg-orange-100 text-orange-800",
                )}

                {renderPermitBucket(
                  "Issued Permits",
                  issuedPermits,
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />,
                  "hsl(145, 65%, 42%)",
                  "bg-green-100 text-green-800",
                )}

                {renderPermitBucket(
                  "Closed Permits",
                  closedPermits,
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />,
                  "hsl(200, 70%, 45%)",
                  "bg-blue-100 text-blue-800",
                )}

                {renderPermitBucket(
                  "Rejected Permits",
                  rejectedPermits,
                  <CircleAlert className="h-4 w-4 text-muted-foreground" />,
                  "hsl(0, 75%, 55%)",
                  "bg-red-100 text-red-800",
                )}

                {renderPermitBucket(
                  "Cancelled Permits",
                  cancelledPermits,
                  <XCircle className="h-4 w-4 text-muted-foreground" />,
                  "hsl(220, 9%, 46%)",
                  "bg-slate-100 text-slate-700",
                )}
              </div>
            </>
          )}
        </div>
      )}

      {view === "create" && (
        <div className="mx-auto w-full max-w-6xl 2xl:max-w-7xl">
          <button
            onClick={() => setView("dashboard")}
            className="mb-4 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </button>

          <div className="mb-6 rounded-xl border bg-card p-4 sm:p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100">
                <ClipboardList className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground sm:text-xl">
                  Raise New Permit
                </h1>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Select Permit
              </label>
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                className="h-11 w-full rounded-lg border bg-white px-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
              >
                <option value="">-- Select type of Permit  --</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {!templateDetails && (
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setView("dashboard")}
                className="w-full sm:w-auto rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
              >
                Back
              </button>
            </div>
          )}

          {templateDetails && (
            <div className="mt-6 space-y-6">
              {/* SECTION 1: Permit Details */}
              <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                <h3 className="mb-4 text-md font-semibold text-slate-800">
                  Permit Details
                </h3>

                <PermitHeaderMeta
                  headerConfig={templateDetails.header_config}
                  formatNo={templateDetails.format_no}
                  refNo={templateDetails.ref_no}
                  issuedDateText={templateDetails.issued_date_text}
                  revisionNo={templateDetails.revision_no}
                  projectId={projectId}
                  permitId={null}
                  ptwNo={reservedPtwNo}
                />

                {/* Render Fields */}
                <div className="grid gap-4 sm:grid-cols-2">
                  {templateDetails.fields_config?.map((fieldConfig) => {
                    const fieldDef = fieldConfig.field_definition;
                    const required =
                      fieldConfig.required_override ??
                      fieldDef.required_default ??
                      false;
                    return (
                      <React.Fragment key={fieldDef.key}>
                        <div
                        className={
                          ["textarea", "multiselect", "multi_select"].includes(
                            fieldDef.field_type,
                          )
                            ? "sm:col-span-2"
                            : ""
                        }
                      >
                        <label className="mb-2 block text-sm font-medium text-slate-700">
                          {fieldDef.key === "contractor_name" ? "Organization / Contractor" : fieldDef.label}{" "}
                          {required && <span className="text-red-500">*</span>}
                        </label>
                        {fieldDef.key === "contractor_name" ? (
                          <div className="flex flex-col gap-1">
                            <input
                              type="text"
                              disabled
                              className="w-full rounded-lg border bg-slate-50 p-3 text-sm text-slate-500 outline-none cursor-not-allowed"
                              value={
                                contractorSelection === "Other"
                                  ? (customContractorName || "")
                                  : isContractorInternal(formData.contractor_name || resolvedOrgContractor)
                                    ? getPermitInternalTeamName(selectedPermit || null, projectId)
                                    : (formData.contractor_name || resolvedOrgContractor || getPermitInternalTeamName(selectedPermit || null, projectId))
                              }
                            />
                            <p className="text-xs text-slate-400">
                              {contractorSelection === "Other"
                                ? "Contractor"
                                : isContractorInternal(formData.contractor_name || resolvedOrgContractor)
                                  ? getPermitInternalTeamName(selectedPermit || null, projectId)
                                  : "Contractor"}
                            </p>
                          </div>
                        ) : fieldDef.key === "permit_applicant" ? (
                          <div className="flex flex-col gap-1">
                            <input
                              type="text"
                              disabled
                              className="w-full rounded-lg border bg-slate-50 p-3 text-sm text-slate-500 outline-none cursor-not-allowed"
                              value={formData.permit_applicant || ""}
                            />
                            <p className="text-xs text-slate-400">
                              Auto-filled from your profile
                            </p>
                          </div>
                        ) : fieldDef.key === "location" ? (
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <select
                              className="min-w-0 flex-1 rounded-lg border p-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 disabled:bg-slate-100 disabled:cursor-not-allowed"
                              value={locationParts.wing}
                              disabled={loadingWings}
                              onChange={(e) =>
                                setLocationParts({
                                  ...locationParts,
                                  wing: e.target.value,
                                })
                              }
                            >
                              <option value="">
                                {loadingWings
                                  ? "Loading Wings..."
                                  : wings.length === 0
                                    ? "Select Wing"
                                    : "Select Wing"}
                              </option>
                              {wings.map((w, index) => {
                                const wingName = getWingOptionLabel(w);
                                if (!wingName) return null;
                                const wingKey =
                                  w?.id ||
                                  w?.building_id ||
                                  w?.tower_id ||
                                  index;
                                return (
                                  <option key={wingKey} value={wingName}>
                                    {wingName}
                                  </option>
                                );
                              })}
                              {/* Preserve previously saved wing value if not in fetched list */}
                              {locationParts.wing &&
                                !wings.some(
                                  (w) =>
                                    getWingOptionLabel(w).toLowerCase() ===
                                    locationParts.wing.toLowerCase(),
                                ) && (
                                  <option value={locationParts.wing}>
                                    {locationParts.wing}
                                  </option>
                                )}
                            </select>
                            <input
                              type="text"
                              placeholder="Floor"
                              className="min-w-0 flex-1 rounded-lg border p-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                              value={locationParts.floor}
                              onChange={(e) =>
                                setLocationParts({
                                  ...locationParts,
                                  floor: e.target.value,
                                })
                              }
                            />
                            <input
                              type="text"
                              placeholder="Area/Flat"
                              className="min-w-0 flex-1 rounded-lg border p-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                              value={locationParts.flat}
                              onChange={(e) =>
                                setLocationParts({
                                  ...locationParts,
                                  flat: e.target.value,
                                })
                              }
                            />
                          </div>
                        ) : fieldDef.field_type === "textarea" ? (
                          <textarea
                            className="w-full rounded-lg border p-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                            rows={3}
                            value={formData[fieldDef.key] || ""}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                [fieldDef.key]: e.target.value,
                              })
                            }
                          />
                        ) : fieldDef.field_type === "multiselect" ||
                          fieldDef.field_type === "multi_select" ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2.5 mt-2">
                            {(
                              fieldDef.validation_rules?.choices ||
                              fieldDef.validation_rules?.options ||
                              fieldDef.options ||
                              []
                            )?.map((opt) => {
                              const optVal = extractOptionValue(opt);
                              const optLabel = extractOptionString(opt);
                              const currentSelected = Array.isArray(formData[fieldDef.key])
                                ? formData[fieldDef.key].map((x) => extractOptionValue(x))
                                : [];
                              const isChecked = currentSelected.includes(optVal);

                              return (
                                <label
                                  key={optVal || optLabel}
                                  className="flex items-center gap-2.5 text-xs sm:text-sm cursor-pointer rounded-xl border bg-slate-50/50 p-2.5 sm:p-3 hover:bg-orange-50 hover:border-orange-200 transition-colors min-w-0"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      const curr = Array.isArray(formData[fieldDef.key])
                                        ? formData[fieldDef.key].map((x) => extractOptionValue(x))
                                        : [];
                                      if (e.target.checked)
                                        setFormData({
                                          ...formData,
                                          [fieldDef.key]: [...curr, optVal],
                                        });
                                      else
                                        setFormData({
                                          ...formData,
                                          [fieldDef.key]: curr.filter(
                                            (x) => x !== optVal,
                                          ),
                                        });
                                    }}
                                    className="h-4 w-4 shrink-0 rounded border-gray-300 text-orange-600 focus:ring-orange-600 accent-orange-600"
                                  />
                                  <span className="font-medium text-slate-700 break-words min-w-0">
                                    {optLabel}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        ) : (
                          <input
                            type={
                              fieldDef.field_type === "number"
                                ? "number"
                                : "text"
                            }
                            className={`rounded-lg border p-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 ${fieldDef.field_type === "number" ? "w-full sm:w-48" : "w-full"}`}
                            value={formData[fieldDef.key] || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setFormData({
                                ...formData,
                                [fieldDef.key]:
                                  fieldDef.field_type === "number" && val !== ""
                                    ? Number(val)
                                    : val,
                              });
                            }}
                          />
                        )}
                      </div>

                      {/* If Internal user and this is workmen_count, render Contractor dropdown beside it */}
                      {isInternalUser &&
                        (fieldDef.key === "workmen_count" ||
                          fieldDef.key === "workman_count" ||
                          (fieldDef.label &&
                            fieldDef.label.toLowerCase().includes("workmen"))) && (
                          <div className="flex flex-col">
                            <label className="mb-2 block text-sm font-medium text-slate-700">
                              Contractor <span className="text-red-500">*</span>
                            </label>
                            <select
                              className="w-full rounded-lg border p-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 disabled:bg-slate-100 disabled:cursor-not-allowed"
                              value={contractorSelection}
                              disabled={loadingContractors}
                              onChange={(e) => {
                                const val = e.target.value;
                                setContractorSelection(val);
                                setContractorError("");
                                if (val === "Other") {
                                  setFormData((prev) => ({
                                    ...prev,
                                    contractor_selection: "Other",
                                    contractor_name: customContractorName.trim(),
                                  }));
                                } else if (val && !isContractorInternal(val)) {
                                  setCustomContractorName("");
                                  setFormData((prev) => ({
                                    ...prev,
                                    contractor_selection: val,
                                    contractor_name: val,
                                  }));
                                } else {
                                  setCustomContractorName("");
                                  setFormData((prev) => ({
                                    ...prev,
                                    contractor_selection: "Internal",
                                    contractor_name: "Internal",
                                  }));
                                }
                              }}
                            >
                              <option value="Internal">
                                {isPermitProject165(selectedPermit || null, projectId)
                                  ? "Arkade Developers Limited (Assigned to Internal Team)"
                                  : "Internal (Assigned to Internal Team)"}
                              </option>
                              {projectContractors
                                .filter(
                                  (c) =>
                                    !isContractorInternal(c.contractorName) &&
                                    c.contractorName.toLowerCase() !== "other"
                                )
                                .map((c) => (
                                  <option
                                    key={c.contractorId || c.contractorName}
                                    value={c.contractorName}
                                  >
                                    {c.contractorName}
                                  </option>
                                ))}
                              <option value="Other">Other</option>
                            </select>

                            {contractorSelection === "Other" && (
                              <div className="mt-2.5 flex flex-col gap-1">
                                <label className="block text-xs font-semibold text-slate-600">
                                  Enter Contractor Name{" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  placeholder="Enter Contractor Name"
                                  className={`w-full rounded-lg border p-2.5 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 ${
                                    contractorError
                                      ? "border-red-500 ring-1 ring-red-100"
                                      : ""
                                  }`}
                                  value={customContractorName}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setCustomContractorName(val);
                                    if (contractorError) setContractorError("");
                                    setFormData((prev) => ({
                                      ...prev,
                                      contractor_selection: "Other",
                                      contractor_name: val,
                                    }));
                                  }}
                                />
                                {contractorError && (
                                  <p className="text-xs text-red-500">
                                    {contractorError}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </section>

              {/* SECTION 2: Permit Description */}
              <PermitDescriptionBox
                description={templateDetails?.description}
                title="Permit Description"
                titleClassName="mb-3 text-md font-semibold text-slate-800"
              />

              {/* SECTION 3: Checklist Points */}
              {(() => {
                const createQuestions =
                  (templateDetails?.checklist_template?.questions?.length
                    ? templateDetails.checklist_template.questions
                    : templateDetails?.questions) || [];
                const createSections =
                  (templateDetails?.checklist_template?.sections?.length
                    ? templateDetails.checklist_template.sections
                    : templateDetails?.sections) ||
                  templateDetails?.checklist_sections ||
                  [];

                if (!createQuestions.length) return null;

                const createSectionGroups = buildChecklistSections({
                  sections: createSections,
                  questions: createQuestions,
                });
                const firstQId = createQuestions[0]?.id;

                return (
                  <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                    <h3 className="mb-4 text-md font-semibold text-slate-800">
                      Checklist Points
                    </h3>
                    <PermitChecklistSectionTabs sectionGroups={createSectionGroups}>
                      {(currentGroup) => (
                        <div className="space-y-4">
                          {currentGroup.questions.map(
                            (q, idx) => {
                              const isTextQuestion = isTextChecklistQuestion(q);
                              const isNumberQuestion = isNumberChecklistQuestion(q);

                              const options =
                                !isTextQuestion && !isNumberQuestion
                                  ? q.options && q.options.length > 0
                                    ? q.options.map((o) =>
                                      typeof o === "object" && o !== null
                                        ? o.value ?? o
                                        : o,
                                    )
                                    : ["YES", "NO", "N/A"]
                                  : [];
                              const currentAnswer = checklistResponse[q.id]?.answer;
                              const currentRemarks =
                                checklistResponse[q.id]?.remarks || "";

                              const isFirstQuestionOverall = q.id === firstQId;
                              const existingQuestionImages = (
                                selectedPermit?.checklist_images || []
                              ).filter((img) => String(img.question_id) === String(q.id));
                              const qNumber = idx + 1;

                              return (
                                <div
                                  key={q.id}
                                  className="rounded-xl border bg-card p-4 sm:p-5 shadow-sm transition-all hover:shadow-md mb-4"
                                >
                                  <div className="mb-4 flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                                    <div className="flex items-start gap-3">
                                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-orange-600 text-xs font-bold text-white shadow-sm">
                                        {qNumber}
                                      </span>

                                      <div className="mt-1 flex-1">
                                        <p className="text-[15px] font-semibold text-slate-800">
                                          {q.question}
                                        </p>
                                      </div>
                                    </div>

                                    {isFirstQuestionOverall && (
                                      <button
                                        type="button"
                                        onClick={openTbtModal}
                                        className="shrink-0 rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 transition-colors hover:bg-orange-100"
                                      >
                                        TBT Attendance
                                      </button>
                                    )}
                                  </div>
                                  <div className="ml-0 sm:ml-11">
                                    {isTextQuestion ? (
                                      <div className="mb-4">
                                        <label className="mb-1.5 block text-xs font-medium text-slate-500">
                                          Answer
                                        </label>
                                        <textarea
                                          rows={3}
                                          placeholder="Enter details..."
                                          className="w-full rounded-lg border border-slate-300 p-2.5 text-sm outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                                          value={extractAnswerString(currentAnswer) || ""}
                                          onChange={(e) =>
                                            setChecklistResponse((prev) => ({
                                              ...prev,
                                              [q.id]: {
                                                ...(prev[q.id] || {}),
                                                question_id: q.id,
                                                answer: e.target.value,
                                                remarks: prev[q.id]?.remarks || currentRemarks || "",
                                              },
                                            }))
                                          }
                                        />
                                      </div>
                                    ) : isNumberQuestion ? (
                                      <div className="mb-4">
                                        <label className="mb-1.5 block text-xs font-medium text-slate-500">
                                          Answer
                                        </label>
                                        <input
                                          type="number"
                                          placeholder="Enter number..."
                                          className="w-full rounded-lg border border-slate-300 p-2.5 text-sm outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                                          value={extractAnswerString(currentAnswer) || ""}
                                          onChange={(e) =>
                                            setChecklistResponse((prev) => ({
                                              ...prev,
                                              [q.id]: {
                                                ...(prev[q.id] || {}),
                                                question_id: q.id,
                                                answer: e.target.value,
                                                remarks: prev[q.id]?.remarks || currentRemarks || "",
                                              },
                                            }))
                                          }
                                        />
                                      </div>
                                    ) : (
                                      <div className="grid grid-cols-3 gap-2 mb-4 w-full sm:flex sm:flex-wrap sm:w-auto sm:gap-2">
                                        {options.map((opt) => {
                                          const optLabel = extractOptionString(opt);
                                          const optVal = extractOptionValue(opt);
                                          const active =
                                            extractAnswerString(currentAnswer).trim().toLowerCase() ===
                                            optVal.toLowerCase();
                                          const normalizedLabel = optLabel
                                            .trim()
                                            .toLowerCase();
                                          const toneClasses =
                                            normalizedLabel === "yes"
                                              ? {
                                                active:
                                                  "border-green-500 bg-green-500 text-white shadow-sm",
                                                idle: "border-green-200 bg-green-50 text-green-700 hover:bg-green-100",
                                              }
                                              : normalizedLabel === "no"
                                                ? {
                                                  active:
                                                    "border-red-500 bg-red-500 text-white shadow-sm",
                                                  idle: "border-red-200 bg-red-50 text-red-700 hover:bg-red-100",
                                                }
                                                : normalizedLabel === "n/a" ||
                                                  normalizedLabel === "na"
                                                  ? {
                                                    active:
                                                      "border-yellow-500 bg-yellow-500 text-white shadow-sm",
                                                    idle: "border-yellow-200 bg-yellow-50 text-yellow-700 hover:bg-yellow-100",
                                                  }
                                                  : {
                                                    active:
                                                      "border-orange-500 bg-orange-500 text-white shadow-sm",
                                                    idle: "border-gray-200 bg-white text-gray-700 hover:bg-orange-50",
                                                  };

                                          return (
                                            <button
                                              key={optVal || optLabel}
                                              type="button"
                                              onClick={() =>
                                                setChecklistResponse((prev) => ({
                                                  ...prev,
                                                  [q.id]: {
                                                    ...(prev[q.id] || {}),
                                                    question_id: q.id,
                                                    answer: optVal,
                                                    remarks:
                                                      prev[q.id]?.remarks ||
                                                      currentRemarks ||
                                                      "",
                                                  },
                                                }))
                                              }
                                              className={`flex items-center justify-center w-full sm:w-auto rounded-lg border px-3 py-2.5 sm:px-4 sm:py-2 text-sm font-semibold transition min-h-[42px] touch-manipulation ${active
                                                ? toneClasses.active
                                                : toneClasses.idle
                                                }`}
                                            >
                                              {optLabel}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    )}

                                    {/* Comment & Photo Area (side-by-side on mobile when space allows, stacked on web) */}
                                    <div className="mt-4 flex flex-col min-[480px]:flex-row sm:flex-col gap-3 sm:gap-0 items-start">
                                      {/* Comment Section */}
                                      <div className="w-full min-[480px]:flex-1 sm:w-full min-w-0">
                                        <label className="mb-1.5 block text-xs font-medium text-slate-500">
                                          Comment
                                        </label>
                                        <input
                                          type="text"
                                          placeholder="Add comment..."
                                          className="w-full rounded-lg border border-slate-300 p-2.5 text-sm outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                                          value={currentRemarks}
                                          onChange={(e) =>
                                            setChecklistResponse((prev) => ({
                                              ...prev,
                                              [q.id]: {
                                                ...(prev[q.id] || {}),
                                                question_id: q.id,
                                                answer:
                                                  prev[q.id]?.answer ||
                                                  currentAnswer ||
                                                  "",
                                                remarks: e.target.value,
                                              },
                                            }))
                                          }
                                        />
                                      </div>

                                      {/* Photo Section */}
                                      <div className="w-full min-[480px]:flex-1 sm:w-full min-w-0">
                                        {existingQuestionImages.length > 0 && (
                                          <PermitChecklistPhotosList images={existingQuestionImages} />
                                        )}

                                        <MultiImageUploadArea
                                          questionId={q.id}
                                          files={checklistImages[q.id] || []}
                                          label="Attach permit checklist photo"
                                          onGalleryChange={handleChecklistImagesChange}
                                          onTakePhoto={handleOpenChecklistCamera}
                                          onRemove={removeChecklistImage}
                                          className="mt-0 sm:mt-4"
                                        />
                                      </div>
                                    </div>

                                    <PermitQuestionDiscussion
                                      logs={selectedPermit?.question_logs?.[String(q.id)]}
                                    />
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      )}
                    </PermitChecklistSectionTabs>
                  </section>
                );
              })()}

              {/* SECTION 4: Special Permit Requirements */}
              {templateDetails?.special_sections?.length > 0 && (
                <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                  <h3 className="mb-4 text-md font-semibold text-slate-800">
                    Special Permit Requirements
                  </h3>
                  {templateDetails.special_sections.map((section) => (
                    <div key={section.key} className="mb-6 last:mb-0">
                      <h4 className="mb-3 text-sm font-semibold text-slate-700">
                        {section.title}
                      </h4>

                      {section.type === "row_table" ? (
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse border border-slate-300 text-sm">
                            <thead>
                              <tr className="bg-slate-100">
                                <th className="border border-slate-300 p-2 text-left w-12">
                                  S.No
                                </th>
                                {section.columns?.map((col) => (
                                  <th
                                    key={col.key}
                                    className="border border-slate-300 p-2 text-left"
                                  >
                                    {col.label}
                                  </th>
                                ))}
                              </tr>
                            </thead>

                            <tbody>
                              {section.rows?.map((row, idx) => (
                                <tr key={row.key}>
                                  <td className="border border-slate-300 p-2">
                                    {idx + 1}
                                  </td>

                                  {section.columns?.map((col) => {
                                    if (
                                      isSpecialCellDisabled(
                                        row,
                                        col,
                                        section,
                                        formData
                                      )
                                    ) {
                                      return (
                                        <td
                                          key={col.key}
                                          className="border border-slate-300 p-2 text-slate-600 bg-slate-50/50"
                                        >
                                          {""}
                                        </td>
                                      );
                                    }

                                    const editableBy = col.editable_by || [
                                      "maker",
                                    ];

                                    const rowAllowed =
                                      !Array.isArray(col.editable_row_keys) ||
                                      col.editable_row_keys.length === 0 ||
                                      col.editable_row_keys.includes(row.key);

                                    const makerCanEdit =
                                      col.editable &&
                                      editableBy.includes("maker") &&
                                      rowAllowed;

                                    if (!makerCanEdit) {
                                      const currentValue =
                                        formData[section.key]?.rows?.[
                                        row.key
                                        ]?.[col.key] ??
                                        row[col.key] ??
                                        "";

                                      return (
                                        <td
                                          key={col.key}
                                          className="border border-slate-300 p-2 text-slate-600"
                                        >
                                          {currentValue || "-"}
                                        </td>
                                      );
                                    }

                                    const currentValue =
                                      formData[section.key]?.rows?.[
                                      row.key
                                      ]?.[col.key] ?? "";

                                    return (
                                      <td
                                        key={col.key}
                                        className="border border-slate-300 p-1"
                                      >
                                        <input
                                          type={
                                            col.field_type === "number"
                                              ? "number"
                                              : "text"
                                          }
                                          className="w-full rounded border-0 p-1.5 text-sm outline-none focus:ring-2 focus:ring-orange-200"
                                          value={currentValue}
                                          onChange={(e) => {
                                            const val = e.target.value;

                                            setFormData((prev) => ({
                                              ...prev,
                                              [section.key]: {
                                                ...(prev[section.key] || {}),
                                                rows: {
                                                  ...(prev[section.key]
                                                    ?.rows || {}),
                                                  [row.key]: {
                                                    ...(prev[section.key]
                                                      ?.rows?.[row.key] ||
                                                      {}),
                                                    [col.key]:
                                                      col.field_type ===
                                                        "number" && val !== ""
                                                        ? Number(val)
                                                        : val,
                                                  },
                                                },
                                              },
                                            }));
                                          }}
                                        />
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>

                          {section.footer_fields?.length > 0 && (
                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                              {section.footer_fields.map((f) => (
                                <div key={f.key}>
                                  <label className="mb-2 block text-sm font-medium text-slate-700">
                                    {f.label}
                                  </label>

                                  <input
                                    type={
                                      f.field_type === "number"
                                        ? "number"
                                        : "text"
                                    }
                                    className="w-full rounded-lg border p-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                                    value={
                                      formData[section.key]?.[f.key] ?? ""
                                    }
                                    onChange={(e) => {
                                      const val = e.target.value;

                                      setFormData((prev) => ({
                                        ...prev,
                                        [section.key]: {
                                          ...(prev[section.key] || {}),
                                          [f.key]:
                                            f.field_type === "number" &&
                                              val !== ""
                                              ? Number(val)
                                              : val,
                                        },
                                      }));
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : section.type === "table" ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                          {section.fields?.map((f) => (
                            <div key={f.key}>
                              <label className="mb-2 block text-sm font-medium text-slate-700">
                                {f.label}
                              </label>

                              <input
                                type={
                                  f.field_type === "number"
                                    ? "number"
                                    : "text"
                                }
                                className="w-full rounded-lg border p-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                                value={formData[section.key]?.[f.key] ?? ""}
                                onChange={(e) => {
                                  const val = e.target.value;

                                  setFormData((prev) => ({
                                    ...prev,
                                    [section.key]: {
                                      ...(prev[section.key] || {}),
                                      [f.key]:
                                        f.field_type === "number" &&
                                          val !== ""
                                          ? Number(val)
                                          : val,
                                    },
                                  }));
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      ) : section.type === "conditional_group" ? (
                        <div className="rounded-lg border border-slate-300 bg-white">
                          <div className="grid grid-cols-1 md:grid-cols-[260px_1fr]">
                            <div className="border-b border-slate-300 p-3 md:border-b-0 md:border-r">
                              <p className="text-sm font-semibold text-slate-800">
                                {section.left_label || section.title}
                                <span className="text-red-500"> *</span>
                              </p>
                            </div>
                            <div className="p-3">
                              <div className="mb-3 flex flex-wrap items-center gap-6">
                                {(section.condition_field?.options || []).map(
                                  (opt) => {
                                    const optVal = extractOptionValue(opt);
                                    const optLabel = extractOptionString(opt);
                                    const currentValue =
                                      formData[section.key]?.[
                                      section.condition_field.key
                                      ] || "";

                                    return (
                                      <label
                                        key={optVal || optLabel}
                                        className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-slate-800"
                                      >
                                        <span>{optLabel}</span>

                                        <input
                                          type="radio"
                                          name={`${section.key}_${section.condition_field.key}`}
                                          value={optVal}
                                          checked={String(currentValue).trim().toLowerCase() === optVal.toLowerCase()}
                                          onChange={(e) => {
                                            const value = e.target.value;

                                            setFormData((prev) => ({
                                              ...prev,
                                              [section.key]: {
                                                ...(prev[section.key] || {}),
                                                [section.condition_field.key]:
                                                  value,
                                              },
                                            }));
                                          }}
                                          className="h-4 w-4 accent-orange-600"
                                        />
                                      </label>
                                    );
                                  },
                                )}
                              </div>

                              {evaluateSectionShowWhen(
                                section.show_when,
                                formData[section.key]
                              ) && (
                                  <div className="border-t border-slate-200 pt-3">
                                    {section.conditional_title && (
                                      <p className="mb-3 text-sm font-bold text-slate-900">
                                        {section.conditional_title}
                                      </p>
                                    )}

                                    <div className="space-y-4">
                                      {(section.fields || []).map((field) => {
                                        const currentValue =
                                          formData[section.key]?.[field.key];

                                        const shouldHide =
                                          !shouldShowSpecialField(
                                            field,
                                            formData[section.key]
                                          );

                                        if (shouldHide) return null;

                                        const editableBy =
                                          field.editable_by || ["maker"];
                                        const canEditField =
                                          !Array.isArray(editableBy) ||
                                          editableBy.includes("maker");

                                        const isShiftChangeIsolationField =
                                          String(field.label || "")
                                            .toLowerCase()
                                            .includes("shift change") &&
                                          String(field.label || "")
                                            .toLowerCase()
                                            .includes("isolation");

                                        const isFieldRequired =
                                          field.required ||
                                          isShiftChangeIsolationField;

                                        if (field.field_type === "select") {
                                          return (
                                            <div key={field.key}>
                                              <label className="mb-2 block text-sm font-semibold text-slate-800">
                                                {field.label}
                                                {isFieldRequired && (
                                                  <span className="text-red-500">
                                                    {" "}
                                                    *
                                                  </span>
                                                )}
                                              </label>

                                              <select
                                                value={currentValue ?? ""}
                                                onChange={(e) => {
                                                  const nextVal = e.target.value;
                                                  setFormData((prev) => {
                                                    const prevSec =
                                                      prev[section.key] || {};
                                                    const updatedSec = {
                                                      ...prevSec,
                                                      [field.key]: nextVal,
                                                    };
                                                    const cleanedSec =
                                                      cleanHiddenSpecialFields(
                                                        section.fields,
                                                        updatedSec
                                                      );
                                                    return {
                                                      ...prev,
                                                      [section.key]:
                                                        cleanedSec,
                                                    };
                                                  });
                                                }}
                                                disabled={!canEditField}
                                                className="w-full rounded-lg border border-slate-300 bg-white p-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 disabled:cursor-not-allowed disabled:bg-slate-100"
                                              >
                                                <option value="">
                                                  {field.placeholder ||
                                                    "Select an option"}
                                                </option>
                                                {(field.options || []).map(
                                                  (option) => {
                                                    const optVal =
                                                      extractOptionValue(
                                                        option
                                                      );
                                                    const optLabel =
                                                      extractOptionString(
                                                        option
                                                      );
                                                    return (
                                                      <option
                                                        key={
                                                          optVal || optLabel
                                                        }
                                                        value={optVal}
                                                      >
                                                        {optLabel}
                                                      </option>
                                                    );
                                                  }
                                                )}
                                              </select>
                                            </div>
                                          );
                                        }

                                        if (
                                          field.field_type === "multi_select"
                                        ) {
                                          const selectedValues = Array.isArray(
                                            currentValue,
                                          )
                                            ? currentValue
                                            : [];

                                          return (
                                            <div key={field.key}>
                                              <label className="mb-2 block text-sm font-semibold text-slate-800">
                                                {field.label}
                                                {isFieldRequired && (
                                                  <span className="text-red-500">
                                                    {" "}
                                                    *
                                                  </span>
                                                )}
                                              </label>
                                                <div className="flex flex-wrap gap-3">
                                                  {(field.options || []).map(
                                                    (option) => {
                                                      const optVal = extractOptionValue(option);
                                                      const optLabel = extractOptionString(option);
                                                      const isChecked = selectedValues
                                                        .map((v) => extractOptionValue(v))
                                                        .includes(optVal);

                                                      return (
                                                        <label
                                                          key={optVal || optLabel}
                                                          className="flex cursor-pointer items-center gap-2 rounded-lg border bg-slate-50 px-3 py-2 text-sm text-slate-700 hover:bg-orange-50"
                                                        >
                                                          <input
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            onChange={(e) => {
                                                              const currNormalized = selectedValues.map((v) => extractOptionValue(v));
                                                              const nextValues = e.target.checked
                                                                ? [...currNormalized, optVal]
                                                                : currNormalized.filter((x) => x !== optVal);

                                                              setFormData((prev) => {
                                                                const prevSec = prev[section.key] || {};
                                                                const updatedSec = {
                                                                  ...prevSec,
                                                                  [field.key]: nextValues,
                                                                };
                                                                const cleanedSec = cleanHiddenSpecialFields(
                                                                  section.fields,
                                                                  updatedSec
                                                                );
                                                                return {
                                                                  ...prev,
                                                                  [section.key]: cleanedSec,
                                                                };
                                                              });
                                                            }}
                                                            className="h-4 w-4 accent-orange-600"
                                                          />

                                                          <span>{optLabel}</span>
                                                        </label>
                                                      );
                                                    },
                                                  )}
                                                </div>
                                            </div>
                                          );
                                        }

                                        return (
                                          <div key={field.key}>
                                            <label className="mb-2 block text-sm font-semibold text-slate-800">
                                              {field.label}
                                              {isFieldRequired && (
                                                <span className="text-red-500">
                                                  {" "}
                                                  *
                                                </span>
                                              )}
                                            </label>

                                            <input
                                              type={
                                                field.field_type === "number"
                                                  ? "number"
                                                  : "text"
                                              }
                                              placeholder={
                                                field.placeholder || ""
                                              }
                                              disabled={!canEditField}
                                              className="w-full rounded-lg border border-slate-300 p-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100 disabled:cursor-not-allowed disabled:bg-slate-100"
                                              value={currentValue ?? ""}
                                              onChange={(e) => {
                                                const val = e.target.value;
                                                const nextVal =
                                                  field.field_type ===
                                                    "number" && val !== ""
                                                    ? Number(val)
                                                    : val;

                                                setFormData((prev) => {
                                                  const prevSec =
                                                    prev[section.key] || {};
                                                  const updatedSec = {
                                                    ...prevSec,
                                                    [field.key]: nextVal,
                                                  };
                                                  const cleanedSec =
                                                    cleanHiddenSpecialFields(
                                                      section.fields,
                                                      updatedSec
                                                    );
                                                  return {
                                                    ...prev,
                                                    [section.key]:
                                                      cleanedSec,
                                                  };
                                                });
                                              }}
                                            />
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500">
                          Unsupported special section type: {section.type}
                        </p>
                      )}
                    </div>
                  ))}
                </section>
              )}

              {/* Form Action Controls */}
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setView("dashboard")}
                  className="w-full sm:w-auto rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => handleCreateDraft()}
                  disabled={isSubmittingPermit}
                  className="w-full sm:w-auto rounded-lg bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-700 shadow-sm transition-all cursor-pointer disabled:opacity-50"
                >
                  {isSubmittingPermit ? "Submitting..." : "Raise Permit"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {view === "closure_review" && selectedPermit && (
        <div className="mx-auto w-full max-w-6xl 2xl:max-w-7xl">
          <button
            onClick={() => {
              setView("dashboard");
              setSelectedPermit(null);
              setSelectedPermitForClosure(null);
            }}
            className="mb-4 inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Dashboard
          </button>

          <div className="mb-6 rounded-xl border bg-card p-5 shadow-sm">
            <h1 className="text-lg font-bold text-foreground sm:text-xl">
              Request Closure -{" "}
              {selectedPermit.template_snapshot?.template_name ||
                selectedPermit.template_name ||
                `Permit #${selectedPermit.id}`}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Review issued permit details before requesting closure.
            </p>
          </div>

          <PermitHeaderMeta
            headerConfig={selectedPermit.template_snapshot?.header_config}
            formatNo={selectedPermit.template_snapshot?.format_no}
            refNo={selectedPermit.template_snapshot?.ref_no}
            issuedDateText={selectedPermit.template_snapshot?.issued_date_text}
            revisionNo={selectedPermit.template_snapshot?.revision_no}
            projectId={selectedPermit.project_id}
            permitId={selectedPermit.id}
            ptwNo={selectedPermit.ptw_no}
            permit={selectedPermit}
          />

          <div className="rounded-xl border bg-white p-6 shadow-sm">
            <div className="mb-6 rounded-lg bg-green-50 p-4">
              <p className="text-sm font-semibold text-green-700">
                Current Status: {formatStatusLabel(selectedPermit.current_status)}
              </p>
            </div>

            {selectedPermit?.template_snapshot?.field_definitions?.length >
              0 && (
                <div className="mb-6 border-b pb-6">
                  <h3 className="mb-4 text-lg font-semibold text-slate-800">
                    Permit Details
                  </h3>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {selectedPermit.template_snapshot.field_definitions.map(
                      (field) => {
                        let value = selectedPermit.form_data?.[field.key];
                        if (field.key === "contractor_name") {
                          const resolved =
                            resolveContractorName(selectedPermit) ||
                            selectedPermit.contractor_name ||
                            selectedPermit.form_data?.contractor_name ||
                            value;
                          const internalDisplay = getPermitInternalTeamName(selectedPermit);
                          value = isContractorInternal(resolved, selectedPermit)
                            ? internalDisplay
                            : resolved || internalDisplay;
                        }
                        const isPpe = isProvidedPpeField(field);

                        return (
                          <div
                            key={field.key}
                            className={`rounded-lg border bg-slate-50 p-3 ${isPpe ? "sm:col-span-2" : ""
                              }`}
                          >
                            <p className="text-xs font-semibold text-slate-500">
                              {field.key === "contractor_name" ? "Organization / Contractor" : field.label}
                            </p>
                            {isPpe ? (
                              <ProvidedPpeBadges value={value} />
                            ) : (
                              <p className="mt-1 text-sm font-medium text-slate-900">
                                {renderFieldValue(value)}
                              </p>
                            )}
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              )}



            {selectedPermit?.template_snapshot?.checklist_questions?.length >
              0 && (() => {
                const closureSectionGroups = buildChecklistSections({
                  sections: selectedPermit.template_snapshot.checklist_sections || [],
                  questions: selectedPermit.template_snapshot.checklist_questions || [],
                });
                const firstQId = selectedPermit.template_snapshot.checklist_questions?.[0]?.id;

                return (
                  <div className="mb-6 border-b pb-6">
                    <h3 className="mb-4 text-lg font-semibold text-slate-800">
                      Checklist Answers
                    </h3>

                    <PermitChecklistSectionTabs sectionGroups={closureSectionGroups}>
                      {(currentGroup) => (
                        <div className="space-y-4">
                          {currentGroup.questions.map(
                            (q, index) => {
                              const response = selectedPermit.checklist_response?.find(
                                (r) => String(r.question_id) === String(q.id),
                              );
                              const isFirstQuestionOverall = q.id === firstQId;
                              const qNumber = index + 1;

                              return (
                                <div
                                  key={q.id}
                                  className="rounded-xl border bg-card p-5 shadow-sm"
                                >
                                  <div className="mb-3 flex items-start gap-3">
                                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-500 text-xs font-bold text-white">
                                      {qNumber}
                                    </span>

                                    <div className="flex-1">
                                      <div className="flex items-start justify-between gap-3">
                                        <p className="text-sm font-semibold text-slate-800">
                                          {q.question}
                                        </p>
                                        {isFirstQuestionOverall && (
                                          <button
                                            type="button"
                                            onClick={openTbtModal}
                                            className="shrink-0 rounded-lg border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 transition-colors hover:bg-orange-100"
                                          >
                                            TBT Attendance
                                          </button>
                                        )}
                                      </div>
                                      <p className="mt-2 flex items-center gap-2 text-sm text-slate-600">
                                        Answer:
                                        <span
                                          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-bold ${getAnswerBadgeClasses(
                                            response?.answer
                                          )}`}
                                        >
                                          {response?.answer || "No answer"}
                                        </span>
                                      </p>

                                      {response?.remarks && (
                                        <p className="mt-1 text-sm text-slate-600">
                                          Remark: {response.remarks}
                                        </p>
                                      )}

                                      <PermitQuestionDiscussion
                                        logs={selectedPermit?.question_logs?.[String(q.id)]}
                                      />
                                    </div>
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      )}
                    </PermitChecklistSectionTabs>
                  </div>
                );
              })()}

            {renderReadonlySpecialSections(selectedPermit)}

            <div className="mb-6">
              <h3 className="mb-4 text-lg font-semibold text-slate-800">
                Closure Request
              </h3>

              {/* Existing / Prior Closure Remarks History */}
              {(() => {
                const existingClosureRemarks = getClosureRemarksList(selectedPermit);
                if (!existingClosureRemarks.length) return null;

                return (
                  <div className="mb-4 space-y-2">
                    <label className="block text-xs font-bold uppercase tracking-wide text-slate-600">
                      Previous Closure Remarks / History
                    </label>
                    <div className="space-y-2">
                      {existingClosureRemarks.map((item, idx) => (
                        <div
                          key={item.id || idx}
                          className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 shadow-2xs"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-slate-900">
                                {item.author}
                              </span>
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold border ${item.badgeColor}`}>
                                {item.roleLabel}
                              </span>
                            </div>
                            {item.date && (
                              <span className="text-[11px] text-slate-500 font-medium">
                                {item.date}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-800 whitespace-pre-wrap leading-relaxed font-medium">
                            {item.remark}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <label className="mb-2 block text-sm font-medium text-slate-700">
                Closure Remarks
              </label>

              <textarea
                className="w-full rounded-lg border p-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                rows={3}
                value={closureRemarks}
                onChange={(e) => setClosureRemarks(e.target.value)}
                placeholder="Enter closure request remarks..."
              />
            </div>

            <div className="flex justify-end gap-3 border-t pt-6">
              <button
                type="button"
                onClick={() => setView("dashboard")}
                className="rounded-lg border px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={isSubmittingPermit}
                onClick={() => handleRequestClosure()}
                className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-50"
              >
                {isSubmittingPermit ? "Submitting..." : "Submit Closure Request"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resubmit View */}
      {view === "resubmit" && selectedPermit && (
        <div className="mx-auto w-full max-w-6xl 2xl:max-w-7xl rounded-2xl bg-white p-6 sm:p-8 shadow-sm border border-slate-200">
          {(() => {
            const isCancelledMode = selectedPermit.current_status === "cancelled";
            const isClosureMode = isClosureRejection(selectedPermit);
            const questions =
              selectedPermit.template_snapshot?.checklist_questions ||
              selectedPermit.template_snapshot?.sections?.flatMap((s) => s.questions || []) ||
              selectedPermit.template_snapshot?.checklist_template?.questions ||
              selectedPermit.template?.checklist_template?.questions ||
              selectedPermit.checklist_questions ||
              templateDetails?.checklist_template?.questions ||
              [];

            const latestRejectLog = getLatestRejectLog(selectedPermit);
            const rejectedQuestionIds = isCancelledMode
              ? (selectedPermit.cancelled_question_ids || []).map(Number)
              : getRejectedQuestionIds(selectedPermit);
            const rejectedSet = new Set(rejectedQuestionIds.map(Number));
            const hasQuestionFeedback = rejectedQuestionIds.length > 0;

            const rejectionReason = isCancelledMode
              ? selectedPermit.latest_cancel_reason || "Checklist point(s) were flagged during cancellation."
              : latestRejectLog?.remarks?.trim() ||
              selectedPermit.last_rejection_remarks?.trim() ||
              "Checklist point(s) were flagged for correction during review.";

            return (
              <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setView("dashboard");
                        setSelectedPermit(null);
                        setResubmitOverallRemarks("");
                      }}
                      className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-100 cursor-pointer"
                    >
                      <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div>
                      <h2 className="text-lg font-bold text-slate-900">
                        {isCancelledMode ? "Resubmit Cancelled Permit" : "Resubmit Permit"}: {selectedPermit.ptw_no || selectedPermit.template?.name || `PTW #${selectedPermit.id}`}
                      </h2>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${isCancelledMode ? "bg-orange-100 text-orange-700" : "bg-red-100 text-red-700"}`}>
                      Status: {formatStatusLabel(getStatus(selectedPermit))}
                    </span>
                    {getPermitRejectionBadges(selectedPermit).map((badge, idx) => (
                      <span
                        key={`${badge.type}-${idx}`}
                        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${badge.className}`}
                      >
                        {badge.label}
                        {badge.isLimitReached ? " · Limit Reached" : ""}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Header Form Data (Locked / Readonly) */}
                {selectedPermit.form_data && Object.keys(selectedPermit.form_data).length > 0 && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                    <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500">
                      Permit General Details (Locked)
                    </h3>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {Object.entries(selectedPermit.form_data).map(([key, val]) => {
                        const isPpe = isProvidedPpeField(key);

                        return (
                          <div
                            key={key}
                            className={`rounded-lg border border-slate-200 bg-white p-2.5 ${isPpe ? "sm:col-span-2 lg:col-span-3" : ""
                              }`}
                          >
                            <div className="text-[11px] font-semibold text-slate-400 capitalize">
                              {key.replace(/_/g, " ")}
                            </div>
                            {isPpe ? (
                              <ProvidedPpeBadges value={val} />
                            ) : (
                              <div className="text-xs font-medium text-slate-800 mt-0.5">
                                {renderFieldValue(val)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Permit Description */}
                <PermitDescriptionBox
                  description={
                    selectedPermit?.template_snapshot?.description ||
                    selectedPermit?.template?.description ||
                    templateDetails?.description
                  }
                  title="Permit Description"
                  titleClassName="mb-3 text-xs font-bold uppercase tracking-wider text-slate-500"
                />

                {/* Checklist Questions Section */}
                {(() => {
                  const resubmitSectionGroups = buildChecklistSections({
                    sections:
                      selectedPermit.template_snapshot?.checklist_sections ||
                      selectedPermit.template_snapshot?.sections ||
                      templateDetails?.checklist_template?.sections ||
                      [],
                    questions: questions,
                  });

                  const getResubmitBadgeInfo = (group) => {
                    if (isCancelledMode && !hasQuestionFeedback) {
                      return {
                        label: "All Editable",
                        colorCls: "bg-blue-600 text-white font-semibold",
                      };
                    }
                    const rejectedInGroup = group.questions.filter((q) =>
                      rejectedSet.has(Number(q.id))
                    ).length;
                    if (rejectedInGroup > 0) {
                      return {
                        label: `${rejectedInGroup} flagged`,
                        colorCls: "bg-red-500 text-white font-bold animate-pulse",
                      };
                    }
                    return null;
                  };

                  return (
                    <div className="space-y-4">
                      <h3 className="text-sm font-bold text-slate-900">
                        Checklist Questions & Corrections
                      </h3>
                      <PermitChecklistSectionTabs
                        sectionGroups={resubmitSectionGroups}
                        getBadgeInfo={getResubmitBadgeInfo}
                      >
                        {(currentGroup) => (
                          <div className="space-y-4">
                            {currentGroup.questions.map((q, idx) => {
                              const qNumber = idx + 1;
                              const canMakerEdit = (!isCancelledMode || hasQuestionFeedback)
                                ? rejectedSet.has(Number(q.id))
                                : true;
                              const isFlaggedPoint = (!isCancelledMode || hasQuestionFeedback)
                                ? rejectedSet.has(Number(q.id))
                                : false;
                              const savedRow = (
                                Array.isArray(selectedPermit?.checklist_response)
                                  ? selectedPermit.checklist_response
                                  : (typeof selectedPermit?.checklist_response === "string"
                                      ? (() => { try { return JSON.parse(selectedPermit.checklist_response); } catch (_) { return []; } })()
                                      : [])
                              ).find((r) => String(r.question_id ?? r.id) === String(q.id));

                              const ansObj = checklistResponse[String(q.id)] || savedRow || {};
                              const currentAns = extractAnswerString(ansObj.answer);
                              const currentRemarks = ansObj.remarks || ansObj.comment || "";
                              const options = Array.isArray(q.options) && q.options.length > 0
                                ? q.options
                                : ["YES", "NO", "N/A"];

                              const isTextQuestion = isTextChecklistQuestion(q);
                              const isNumberQuestion = isNumberChecklistQuestion(q);

                              return (
                                <div
                                  key={q.id || idx}
                                  className={`rounded-xl border p-4 sm:p-5 transition-all ${
                                    isFlaggedPoint
                                      ? "border-red-300 bg-red-50/50 shadow-sm"
                                      : canMakerEdit
                                      ? "border-emerald-300 bg-emerald-50/30"
                                      : "border-slate-200 bg-slate-50/60 opacity-80"
                                  }`}
                                >
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-bold text-slate-400">
                                          Q{qNumber}.
                                        </span>
                                        <span className="text-sm font-semibold text-slate-900">
                                          {q.question}
                                        </span>
                                        {q.is_required && (
                                          <span className="text-red-500 font-bold">*</span>
                                        )}
                                      </div>
                                    </div>
                                    <div>
                                      {isFlaggedPoint ? (
                                        <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold text-red-700 border border-red-200">
                                          {isCancelledMode ? "Cancelled Point - Correction Required" : "Rejected - Correction Required"}
                                        </span>
                                      ) : canMakerEdit ? (
                                        <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 border border-emerald-200">
                                          Editable
                                        </span>
                                      ) : (
                                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500 border border-slate-200">
                                          Locked
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Answer Options / Text Field */}
                                  {isTextChecklistQuestion(q) ? (
                                    <div className="mt-3">
                                      <textarea
                                        rows={3}
                                        disabled={!canMakerEdit}
                                        value={currentAns || ""}
                                        onChange={(e) => {
                                          if (!canMakerEdit) return;
                                          const val = e.target.value;
                                          setChecklistResponse((prev) => ({
                                            ...prev,
                                            [String(q.id)]: {
                                              ...(prev[String(q.id)] || {}),
                                              question_id: q.id,
                                              answer: val,
                                              remarks: prev[String(q.id)]?.remarks ?? currentRemarks ?? "",
                                            },
                                          }));
                                        }}
                                        className="w-full rounded-lg border border-slate-300 bg-white p-2.5 text-xs text-slate-800 focus:border-orange-500 focus:outline-none disabled:bg-slate-100 disabled:text-slate-500"
                                        placeholder="Enter details / answer..."
                                      />
                                    </div>
                                  ) : isNumberChecklistQuestion(q) ? (
                                    <div className="mt-3">
                                      <input
                                        type="number"
                                        disabled={!canMakerEdit}
                                        value={currentAns || ""}
                                        onChange={(e) => {
                                          if (!canMakerEdit) return;
                                          const val = e.target.value;
                                          setChecklistResponse((prev) => ({
                                            ...prev,
                                            [String(q.id)]: {
                                              ...(prev[String(q.id)] || {}),
                                              question_id: q.id,
                                              answer: val,
                                              remarks: prev[String(q.id)]?.remarks ?? currentRemarks ?? "",
                                            },
                                          }));
                                        }}
                                        className="w-full rounded-lg border border-slate-300 bg-white p-2.5 text-xs text-slate-800 focus:border-orange-500 focus:outline-none disabled:bg-slate-100 disabled:text-slate-500"
                                        placeholder="Enter number..."
                                      />
                                    </div>
                                  ) : (
                                    <div className="mt-3 grid grid-cols-3 gap-2 w-full sm:flex sm:flex-wrap sm:w-auto sm:gap-2">
                                      {options.map((opt) => {
                                        const optStr = extractOptionString(opt);
                                        const optVal = extractOptionValue(opt);
                                        const isSelected =
                                          String(currentAns || "").trim().toLowerCase() ===
                                          optVal.toLowerCase();
                                        const normalizedLabel = (optStr || optVal || "").trim().toLowerCase();
                                        const toneClasses =
                                          normalizedLabel === "yes"
                                            ? {
                                                active: "border-green-500 bg-green-500 text-white shadow-sm",
                                                idle: canMakerEdit
                                                  ? "border-green-200 bg-green-50 text-green-700 hover:bg-green-100 cursor-pointer"
                                                  : "border-green-100 bg-green-50/40 text-green-600/50 opacity-50 cursor-not-allowed",
                                              }
                                            : normalizedLabel === "no"
                                            ? {
                                                active: "border-red-500 bg-red-500 text-white shadow-sm",
                                                idle: canMakerEdit
                                                  ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100 cursor-pointer"
                                                  : "border-red-100 bg-red-50/40 text-red-600/50 opacity-50 cursor-not-allowed",
                                              }
                                            : normalizedLabel === "n/a" || normalizedLabel === "na" || normalizedLabel === "not applicable"
                                            ? {
                                                active: "border-yellow-500 bg-yellow-500 text-white shadow-sm",
                                                idle: canMakerEdit
                                                  ? "border-yellow-200 bg-yellow-50 text-yellow-700 hover:bg-yellow-100 cursor-pointer"
                                                  : "border-yellow-100 bg-yellow-50/40 text-yellow-600/50 opacity-50 cursor-not-allowed",
                                              }
                                            : {
                                                active: "border-orange-500 bg-orange-500 text-white shadow-sm",
                                                idle: canMakerEdit
                                                  ? "border-gray-200 bg-white text-gray-700 hover:bg-orange-50 cursor-pointer"
                                                  : "border-slate-200 bg-slate-50 text-slate-400 opacity-50 cursor-not-allowed",
                                              };

                                        return (
                                          <button
                                            key={optVal || optStr}
                                            type="button"
                                            disabled={!canMakerEdit}
                                            onClick={() => {
                                              if (!canMakerEdit) return;
                                              setChecklistResponse((prev) => ({
                                                ...prev,
                                                [String(q.id)]: {
                                                  ...(prev[String(q.id)] || {}),
                                                  question_id: q.id,
                                                  answer: optVal,
                                                  remarks: prev[String(q.id)]?.remarks ?? currentRemarks ?? "",
                                                },
                                              }));
                                            }}
                                            className={`flex items-center justify-center w-full sm:w-auto rounded-lg border px-3 py-2.5 sm:px-3.5 sm:py-1.5 text-xs font-bold transition-all min-h-[42px] sm:min-h-0 touch-manipulation ${
                                              isSelected
                                                ? `${toneClasses.active} ${isFlaggedPoint && canMakerEdit ? "ring-2 ring-offset-1 ring-orange-300" : ""}`
                                                : toneClasses.idle
                                            }`}
                                          >
                                            {optStr}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}

                                  {/* Maker Comment Input for Non-Flagged Editable Points */}
                                  {!isFlaggedPoint && canMakerEdit && (
                                    <div className="mt-3">
                                      <label className="mb-1 block text-xs font-medium text-slate-500">
                                        Comment (Optional)
                                      </label>
                                      <input
                                        type="text"
                                        disabled={!canMakerEdit}
                                        value={resubmitQuestionComments[q.id] ?? currentRemarks ?? ""}
                                        onChange={(e) => {
                                          if (!canMakerEdit) return;
                                          const val = e.target.value;
                                          setResubmitQuestionComments((prev) => ({
                                            ...prev,
                                            [q.id]: val,
                                          }));
                                          setChecklistResponse((prev) => ({
                                            ...prev,
                                            [String(q.id)]: {
                                              ...(prev[String(q.id)] || {}),
                                              question_id: q.id,
                                              answer: prev[String(q.id)]?.answer || currentAns || "",
                                              remarks: val,
                                            },
                                          }));
                                        }}
                                        placeholder="Add comment / remark for this question..."
                                        className="w-full rounded-lg border border-slate-300 bg-white p-2.5 text-xs text-slate-800 placeholder-slate-400 outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 disabled:bg-slate-100 disabled:text-slate-500"
                                      />
                                    </div>
                                  )}

                                  {/* Maker Remark Display for Locked Points with Existing Comments */}
                                  {!isFlaggedPoint && !canMakerEdit && currentRemarks && (
                                    <div className="mt-2.5 rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-600">
                                      <span className="font-semibold text-slate-700">Maker Remark:</span> {currentRemarks}
                                    </div>
                                  )}

                                  {/* Checker Remark Display */}
                                  {isFlaggedPoint && (() => {
                                    const rawRemark = isCancelledMode
                                      ? (selectedPermit.latest_cancel_question_comments || []).find((c) => Number(c.question_id) === Number(q.id)) || getCheckerRejectionRemarkForQuestion(selectedPermit, q.id)
                                      : getCheckerRejectionRemarkForQuestion(selectedPermit, q.id);
                                    if (!rawRemark?.comment) return null;
                                    const checkerRemark = {
                                      ...rawRemark,
                                      created_at:
                                        rawRemark.created_at ||
                                        rawRemark.timestamp ||
                                        rawRemark.performed_at ||
                                        (isCancelledMode
                                          ? selectedPermit.latest_cancelled_at || selectedPermit.cancelled_at
                                          : getLatestRejectLog(selectedPermit)?.performed_at || selectedPermit.rejected_at) ||
                                        "",
                                    };

                                    return (
                                      <div className="mt-3.5 rounded-xl border border-red-200 bg-red-100/80 p-3.5 shadow-2xs">
                                        <div className="flex items-center justify-between text-xs font-bold text-red-900">
                                          <div className="flex items-center gap-1.5">
                                            <CircleAlert className="h-4 w-4 text-red-600 shrink-0" />
                                            <span>{isCancelledMode ? "Checker Cancellation Remark" : "Checker Rejection Remark"}</span>
                                          </div>
                                          <div className="flex flex-wrap items-center gap-1.5 text-right text-[11px]">
                                            {(checkerRemark.user_name || checkerRemark.created_by_name) && (
                                              <span className="font-semibold text-red-700">
                                                By {checkerRemark.user_name || checkerRemark.created_by_name}
                                              </span>
                                            )}
                                            {checkerRemark.created_at && (
                                              <span className="font-medium text-red-600/90">
                                                {(checkerRemark.user_name || checkerRemark.created_by_name) && "• "}
                                                {formatPermitDateTime(checkerRemark.created_at)}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        <div className="mt-2 rounded-lg border border-red-200/60 bg-white/90 p-2.5 text-xs font-semibold text-red-950 leading-relaxed shadow-2xs">
                                          "{checkerRemark.comment}"
                                        </div>
                                      </div>
                                    );
                                  })()}

                                  {/* Maker Resubmission Remark Input */}
                                  {isFlaggedPoint && (
                                    <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50/50 p-3.5">
                                      <div className="mb-1.5 flex items-center justify-between text-xs font-bold text-blue-900">
                                        <div className="flex items-center gap-1.5">
                                          <Send className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                                          <span>
                                            {isCancelledMode
                                              ? "Your Cancellation Correction Remark (Optional)"
                                              : "Your Resubmission Remark / Corrective Action"}
                                          </span>
                                          {!isCancelledMode && <span className="text-red-500 font-bold">*</span>}
                                        </div>
                                      </div>
                                      <textarea
                                        rows={2}
                                        value={resubmitQuestionComments[q.id] ?? currentRemarks ?? ""}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          setResubmitQuestionComments((prev) => ({
                                            ...prev,
                                            [q.id]: val,
                                          }));
                                          setChecklistResponse((prev) => ({
                                            ...prev,
                                            [String(q.id)]: {
                                              ...(prev[String(q.id)] || {}),
                                              question_id: q.id,
                                              answer: prev[String(q.id)]?.answer || currentAns || "",
                                              remarks: val,
                                            },
                                          }));
                                        }}
                                        placeholder={
                                          isCancelledMode
                                            ? "Describe the corrective action taken for this cancelled point..."
                                            : "Describe the corrective action taken for this rejected point (Required)..."
                                        }
                                        className="w-full rounded-lg border border-blue-200 bg-white p-2.5 text-xs text-slate-800 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-200"
                                      />
                                    </div>
                                  )}

                                  {/* Readonly Log History - Hidden during giving response for rejected points */}
                                  {!isFlaggedPoint && (
                                    <div className="mt-3 border-t border-slate-200/60 pt-2">
                                      <PermitQuestionDiscussion
                                        permit={selectedPermit}
                                        questionId={q.id}
                                        readonly={true}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </PermitChecklistSectionTabs>
                    </div>
                  );
                })()}

                {/* Readonly Signatures Section */}
                <PTWSignOffSection permit={selectedPermit} title="Completed Signatures" />

                {/* Closure Resubmission Remark Input Card */}
                {isClosureMode && (
                  <div className="rounded-xl border border-blue-200 bg-blue-50/70 p-4 shadow-sm space-y-3">
                    <div className="flex items-center gap-2 text-sm font-bold text-blue-900">
                      <MessageSquare className="h-4 w-4 text-blue-600" />
                      <span>Closure Resubmission Remark *</span>
                    </div>
                    <p className="text-xs text-blue-700 font-medium">
                      Checklist questions are locked because this permit was rejected during closure. Please provide your explanation or actions taken regarding the checker's closure rejection comment above to enable resubmission.
                    </p>
                    <textarea
                      value={resubmitOverallRemarks}
                      onChange={(e) => setResubmitOverallRemarks(e.target.value)}
                      rows={3}
                      placeholder="Enter your overall remark / response to the checker's closure rejection comment..."
                      className="w-full rounded-lg border border-blue-300 bg-white p-3 text-xs text-slate-800 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                )}

                {/* Rejection / Cancellation Banner (Moved to Bottom) */}
                {rejectionReason ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4 shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm font-bold text-red-700">
                      <div className="flex items-center gap-2">
                        <CircleAlert className="h-5 w-5" />
                        {isCancelledMode
                          ? (hasQuestionFeedback
                              ? "Cancellation Reason & Required Corrections"
                              : "Permit Cancelled — Overall Reason")
                          : selectedPermit.closure_rejection_count > 0
                            ? "Closure Rejection Reason & Required Corrections"
                            : "Approval Rejection Reason & Required Corrections"}
                      </div>
                      <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-bold text-red-700">
                        {isCancelledMode
                          ? `Cancellation ${selectedPermit.cancel_count || 1} of 3`
                          : selectedPermit.closure_rejection_count > 0
                            ? `Closure Rejection ${selectedPermit.closure_rejection_count || 1} of 3`
                            : `Approval Rejection ${selectedPermit.issue_rejection_count || 1} of 3`}
                      </span>
                    </div>
                    <p className="mt-1.5 text-sm font-medium text-red-900">
                      {rejectionReason}
                    </p>
                    {isCancelledMode ? (
                      selectedPermit.latest_cancelled_by_name && (
                        <p className="mt-1 text-xs text-red-600">
                          Cancelled by <span className="font-semibold">{selectedPermit.latest_cancelled_by_name}</span>
                          {selectedPermit.latest_cancelled_at_display ? ` on ${selectedPermit.latest_cancelled_at_display}` : ""}
                        </p>
                      )
                    ) : (
                      latestRejectLog && (
                        <p className="mt-1 text-xs text-red-600">
                          Rejected by <span className="font-semibold">{latestRejectLog.performed_by_name || "Checker"}</span> on {formatPermitDateTime(latestRejectLog.performed_at || latestRejectLog.created_at)}
                        </p>
                      )
                    )}
                    {isCancelledMode && (
                      <div className="mt-2.5 rounded-lg border border-red-200/70 bg-white/80 p-2.5 text-xs text-red-800 font-medium">
                        {hasQuestionFeedback
                          ? `Checker flagged ${rejectedQuestionIds.length} specific checklist question(s) below for correction. Non-flagged questions remain locked.`
                          : "No individual checklist questions were flagged. All questions are editable — please verify/update necessary answers and resubmit."}
                      </div>
                    )}
                  </div>
                ) : null}

                {!isCancelledMode && !isClosureMode && rejectedQuestionIds.length === 0 && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
                    <p className="text-sm font-semibold text-amber-800">
                      No specific checklist point was marked for correction by the checker. Please contact the checker or admin before resubmitting.
                    </p>
                  </div>
                )}

                {/* Sticky Action Footer */}
                <div className="sticky bottom-4 z-20 mt-6 flex items-center justify-end gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl">
                  <button
                    type="button"
                    onClick={() => {
                      setView("dashboard");
                      setSelectedPermit(null);
                      setResubmitOverallRemarks("");
                    }}
                    className="rounded-xl border border-slate-300 px-5 py-2.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => handleResubmitRejectedPermit()}
                    disabled={
                      isSubmittingPermit ||
                      (isClosureMode
                        ? !resubmitOverallRemarks.trim()
                        : !isCancelledMode && rejectedQuestionIds.length === 0)
                    }
                    className="rounded-xl bg-orange-600 px-6 py-2.5 text-xs font-bold text-white shadow-md hover:bg-orange-700 disabled:opacity-50 cursor-pointer"
                  >
                    {isSubmittingPermit ? "Resubmitting..." : "Sign & Resubmit"}
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* View Model */}
      {view === "view" && selectedPermit && (
        <PermitReadonlyView
          permit={selectedPermit}
          userType="maker"
          titlePrefix="View Permit"
          onBack={() => {
            setView("dashboard");
            setSelectedPermit(null);
          }}
        />
      )}

      <TbtAttendanceModal
        isOpen={tbtModalOpen}
        onClose={() => setTbtModalOpen(false)}
        mode={selectedPermit ? "api" : "local"}
        localRows={tbtRows}
        setLocalRows={setTbtRows}
        localSheet={tbtSheet}
        setLocalSheet={setTbtSheet}
        firstQuestionId={getFirstChecklistQuestion()?.id}
        permit={selectedPermit}
        projectId={projectId || selectedPermit?.project_id || resolveActiveProjectId?.()}
        readonly={Boolean(selectedPermit)}
      />

      {/* Signature Modal */}
      <PermitSignatureModal
        isOpen={signatureModalOpen}
        onClose={() => {
          setSignatureModalOpen(false);
          setPendingAction(null);
        }}
        onSignatureSuccess={(sigData, sigMeta) => {
          setSignatureModalOpen(false);
          if (pendingAction) pendingAction(sigData, sigMeta);
        }}
        consentConfig={(() => {
          if (view === "closure_review") {
            const closureConsent =
              selectedPermit?.current_signature_consent?.enabled
                ? selectedPermit.current_signature_consent
                : getConsentConfigForTemplateAction(
                    selectedPermit?.template_snapshot || templateDetails,
                    "request_closure",
                    "issued"
                  );
            return {
              enabled: true,
              text:
                closureConsent?.text ||
                selectedPermit?.current_signature_consent?.text ||
                getPermitMakerDisclaimer(selectedPermit),
              signature_box_key:
                closureConsent?.signature_box_key ||
                selectedPermit?.current_signature_consent?.signature_box_key ||
                "closure_applicant_signature",
            };
          }
          return selectedPermit?.current_signature_consent?.enabled
            ? selectedPermit.current_signature_consent
            : {
                enabled: true,
                text:
                  selectedPermit?.current_signature_consent?.text ||
                  getConsentConfigForTemplateAction(templateDetails, "submit", "draft")?.text ||
                  getPermitMakerDisclaimer(selectedPermit),
                signature_box_key:
                  selectedPermit?.current_signature_consent?.signature_box_key ||
                  getConsentConfigForTemplateAction(templateDetails, "submit", "draft")?.signature_box_key ||
                  "issue_maker_signature",
              };
        })()}
      />

      {/* Camera Capture Modal */}
      <SafetyCameraCaptureModal
        open={cameraOpen}
        title="Take Permit Photo"
        onClose={() => setCameraOpen(false)}
        onCapture={handleChecklistCameraCapture}
      />

      {/* Image Annotation Modal */}
      <SafetyImageAnnotationModal
        open={annotationOpen}
        file={annotationFile}
        onClose={handleAnnotationClose}
        onSave={handleAnnotatedChecklistImageSave}
      />
    </div>
  );
}
